import React, { useState, useEffect, useRef, startTransition } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform, useMotionTemplate, AnimatePresence, useScroll, useMotionValueEvent, useInView } from 'motion/react';
import { ArrowRight, ArrowDown, ShieldCheck, MessageSquare, Sliders, Sparkles, Mic2, Zap, Brain, Volume2, Waves, Heart, Languages, Smile, Play, Pause, X, Check, FileText, Speaker, Database, Users, Layers, Tag, ChevronDown, ChevronLeft, ChevronRight, Filter, Menu } from 'lucide-react';
const DiagnosticModal = React.lazy(() => import('./components/DiagnosticModal'));
import ContactScoping from './components/ContactScoping';
import Navbar from './components/Navbar';
import { ProjectBriefOverlay, StudioFormOverlay, DatasetEnquiryOverlay } from './components/ProjectBriefDrawer';
import QuickConnectForm from './components/QuickConnectForm';
import UseCasesSection from './components/UseCasesSection';

// Map of section IDs that differ between mobile and desktop
const responsiveIds: Record<string, string> = {
  'datasets': 'datasets-dt',
  'why-india': 'why-india-mobile',
};

// Scroll helper — manually scrolls #main-scroll-container to a target element by ID
const scrollToId = (id: string) => {
  const isMobile = window.innerWidth < 768;
  // Resolve responsive IDs: mobile uses default, desktop uses mapped alt (and vice versa)
  let resolvedId = id;
  if (isMobile && id === 'why-india') resolvedId = 'why-india-mobile';
  if (!isMobile && id === 'datasets') resolvedId = 'datasets-dt';
  if (!isMobile && id === 'why-india-mobile') resolvedId = 'why-india';
  if (isMobile && id === 'datasets-dt') resolvedId = 'datasets';
  const container = document.getElementById('main-scroll-container');
  const target = document.getElementById(resolvedId);
  if (!container || !target) return;
  let top = 0;
  let el: HTMLElement | null = target;
  while (el && el !== container) {
    top += el.offsetTop;
    el = el.offsetParent as HTMLElement | null;
  }
  container.scrollTo({ top, behavior: 'smooth' });
};

const usps = [
  {
    id: 0,
    title: "Real-World Code-Switching",
    subtitle: "Complex & Natural",
    description: "Standard datasets teach your model textbook Hindi. We teach it how Indians actually talk \u2014 capturing natural, complex code-switched speech across 2\u20133 languages in a single breath.",
    icon: <MessageSquare className="w-6 h-6 text-white" />,
    image: "/code-switching.webp",
    bgPosition: "85% center",
    cta: "Know More",
    tagline: "How India Actually Talks.",
    targetId: "usp1"
  },
  {
    id: 1,
    title: "Voqals\nQuality\nStandard",
    subtitle: "Studio Certified",
    description: "Your model shouldn\u2019t learn from electrical hum. Our proprietary studio certification guarantees \u221260dB noise floor, under 200ms RT60, and zero electrical interference.",
    icon: <Sliders className="w-6 h-6 text-white" />,
    image: "/voqals-quality.webp",
    bgPosition: "80% center",
    cta: "Know More",
    tagline: "Train On Speech. Not Noise.",
    targetId: "usp2"
  },
  {
    id: 2,
    title: "AI That Sounds Human",
    subtitle: "Directed Performance",
    description: "Flat recordings produce flat AI. Every Voqals session is directed by experienced audio professionals who extract real emotion, genuine intent, and naturalistic delivery from every talent.",
    icon: <Sparkles className="w-6 h-6 text-white" />,
    image: "/directed-performance.webp",
    cta: "Know More",
    tagline: "Every emotion. Directed. Delivered.",
    targetId: "usp3"
  }
];
const RotatingUSPCards = () => {
  const [active, setActive] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    if (isHovered) return;
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % usps.length);
    }, 5000); // Sped up for more dynamism
    return () => clearInterval(interval);
  }, [isHovered]);
  return (
    <div 
      className="relative w-full max-w-[var(--card-max-w)] h-[var(--card-height)] mx-auto perspective-1000"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <AnimatePresence mode="popLayout">
        {usps.map((usp, index) => {
          const offset = (index - active + usps.length) % usps.length;
          const isTop = offset === 0;
          
          return (
            <motion.div
              key={usp.id}
              className="absolute inset-x-0 top-0 h-[var(--card-inner-h)] rounded-[clamp(16px,1.4vw,24px)] p-[var(--card-padding)] flex flex-col border border-white/[0.08] bg-[#0a0a0a] cursor-pointer overflow-hidden group shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
              animate={{
                y: offset * 16,
                scale: 1 - offset * 0.04,
                opacity: isTop ? 1 : Math.max(0, 1 - offset * 0.3),
                zIndex: usps.length - offset,
                rotateX: offset * 2,
              }}
              whileHover={isTop ? { scale: 1.015, y: -4, rotateX: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => {
                if (isTop) {
                  scrollToId(usp.targetId);
                } else {
                  setActive(index);
                }
              }}
            >
              {/* Background image — pushed to the right */}
              <motion.div
                className="absolute inset-0 bg-cover"
                style={{ backgroundImage: `url(${usp.image})`, backgroundPosition: usp.bgPosition || 'center' }}
                animate={{ scale: isTop && isHovered ? 1.06 : 1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
              />
              {/* Left glass overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0a0e1a]/95 via-[#0a0e1a]/60 to-transparent" />
              <div className="absolute inset-y-0 left-0 w-[60%] backdrop-blur-md" style={{ maskImage: 'linear-gradient(to right, black 40%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to right, black 40%, transparent 100%)' }} />
              <div className="absolute inset-0 bg-gradient-to-r from-[#56CFFC]/[0.04] via-transparent to-transparent" />

              {/* Content — left aligned, spread top to bottom */}
              <div className="relative z-10 w-[55%] flex flex-col justify-between h-full">
                <p className="text-[clamp(10px,0.7vw,14px)] font-semibold uppercase tracking-[0.2em] text-[#56CFFC]">{usp.subtitle}</p>
                <div>
                  <p className="font-display font-black text-[clamp(24px,2.2vw,48px)] text-white leading-[1.1] tracking-tight mb-[clamp(4px,0.3vw,8px)] whitespace-pre-line">{usp.title}</p>
                  <p className="text-[clamp(12px,0.85vw,17px)] text-white/70 font-normal">{usp.tagline}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); scrollToId(usp.targetId); }}
                  className="flex items-center gap-[clamp(6px,0.5vw,12px)] px-[clamp(14px,1.1vw,24px)] py-[clamp(8px,0.5vw,14px)] rounded-full border border-white/20 bg-white/5 backdrop-blur-sm text-[clamp(9px,0.65vw,14px)] font-bold uppercase tracking-[0.1em] text-white/90 hover:bg-white/10 hover:border-white/30 transition-all group/btn w-fit"
                >
                  {usp.cta} <ArrowRight className="w-[clamp(12px,0.8vw,18px)] h-[clamp(12px,0.8vw,18px)] transform group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
      
      {/* Navigation Arrows + Pagination Dots */}
      <div className="absolute -bottom-2 left-0 right-0 flex justify-center items-center gap-[clamp(8px,0.8vw,16px)] z-50">
        <button
          onClick={() => setActive((prev) => (prev - 1 + usps.length) % usps.length)}
          className="w-[clamp(24px,1.6vw,32px)] h-[clamp(24px,1.6vw,32px)] rounded-full border border-white/20 bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all"
          aria-label="Previous card"
        >
          <ChevronLeft className="w-[clamp(12px,0.8vw,16px)] h-[clamp(12px,0.8vw,16px)]" />
        </button>
        <div className="flex items-center gap-1">
          {usps.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setActive(idx)}
              className="p-[9px] group"
              aria-label={`Go to slide ${idx + 1}`}
            >
              <span className={`block h-1.5 rounded-full transition-all duration-300 ${active === idx ? 'w-8 bg-[#56CFFC] shadow-[0_0_10px_rgba(86,207,252,0.5)]' : 'w-2 bg-[#EEEEEE]/30 group-hover:bg-[#EEEEEE]/50'}`} />
            </button>
          ))}
        </div>
        <button
          onClick={() => setActive((prev) => (prev + 1) % usps.length)}
          className="w-[clamp(24px,1.6vw,32px)] h-[clamp(24px,1.6vw,32px)] rounded-full border border-white/20 bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all"
          aria-label="Next card"
        >
          <ChevronRight className="w-[clamp(12px,0.8vw,16px)] h-[clamp(12px,0.8vw,16px)]" />
        </button>
      </div>
    </div>
  );
};
/* ── USP1: Full Expanded Widget ── */
interface Usp1WidgetProps {
  datasetType: 'standard' | 'voqals';
  setDatasetType: (v: 'standard' | 'voqals') => void;
  isPlaying: boolean;
  setIsPlaying: (v: boolean) => void;
  playbackProgress: number;
  setPlaybackProgress: (v: number) => void;
  detailsOpacity?: any; // MotionValue<number> from useTransform — controls transcript/metadata visibility on mobile
  detailsBlur?: any; // MotionValue<string> — CSS filter blur for transcript teaser
  isExpanded?: boolean; // true when widget is in fullscreen state on mobile
  visible?: boolean; // true once section scrolls into view — gates audio loading
}
// Downsample a channel's Float32Array into `barCount` peak values (0–100)
function downsampleChannel(channelData: Float32Array, barCount: number): number[] {
  const blockSize = Math.floor(channelData.length / barCount);
  const peaks: number[] = [];
  for (let i = 0; i < barCount; i++) {
    let max = 0;
    const start = i * blockSize;
    for (let j = start; j < start + blockSize && j < channelData.length; j++) {
      const abs = Math.abs(channelData[j]);
      if (abs > max) max = abs;
    }
    peaks.push(max * 100);
  }
  // Normalize so the loudest bar is 95%
  const maxPeak = Math.max(...peaks, 0.01);
  return peaks.map(p => Math.max(3, (p / maxPeak) * 95));
}

const Usp1ExpandedWidget = ({ datasetType, setDatasetType, isPlaying, setIsPlaying, playbackProgress, setPlaybackProgress, detailsOpacity, detailsBlur, isExpanded, visible = false }: Usp1WidgetProps) => {
  const voqalsAudioRef = useRef<HTMLAudioElement | null>(null);
  const standardAudioRef = useRef<HTMLAudioElement | null>(null);
  const rafRef = useRef<number>(0);
  const [voqalsDuration, setVoqalsDuration] = useState(0);
  const [standardDuration, setStandardDuration] = useState(0);
  const toggleContainerRef = useRef<HTMLDivElement>(null);
  const [toggleIndicator, setToggleIndicator] = useState({ left: 0, width: 0 });
  const [voqalsLeftWaveform, setVoqalsLeftWaveform] = useState<number[]>([]);
  const [voqalsRightWaveform, setVoqalsRightWaveform] = useState<number[]>([]);
  const [standardLeftWaveform, setStandardLeftWaveform] = useState<number[]>([]);
  const [standardRightWaveform, setStandardRightWaveform] = useState<number[]>([]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const barCount = isMobile ? 100 : 200;

  const duration = datasetType === 'voqals' ? voqalsDuration : standardDuration;

  // Load Voqals audio + decode stereo waveform (deferred until section visible)
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const audioFile = '/Audio/USP1.wav';
    const audio = new Audio(audioFile);
    voqalsAudioRef.current = audio;
    audio.addEventListener('loadedmetadata', () => setVoqalsDuration(audio.duration));
    audio.addEventListener('ended', () => { setIsPlaying(false); setPlaybackProgress(0); });
    fetch(audioFile)
      .then(r => r.arrayBuffer())
      .then(buf => new AudioContext().decodeAudioData(buf))
      .then(decoded => {
        if (cancelled) return;
        const left = decoded.getChannelData(0);
        const right = decoded.numberOfChannels > 1 ? decoded.getChannelData(1) : left;
        setVoqalsLeftWaveform(downsampleChannel(left, barCount));
        setVoqalsRightWaveform(downsampleChannel(right, barCount));
      })
      .catch(() => {});
    return () => { cancelled = true; audio.pause(); audio.src = ''; };
  }, [barCount, visible]);

  // Load Standard audio + decode waveform (deferred until section visible)
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const audioFile = '/Audio/UPS1standard.mp3';
    const audio = new Audio(audioFile);
    standardAudioRef.current = audio;
    audio.addEventListener('loadedmetadata', () => setStandardDuration(audio.duration));
    audio.addEventListener('ended', () => { setIsPlaying(false); setPlaybackProgress(0); });
    fetch(audioFile)
      .then(r => r.arrayBuffer())
      .then(buf => new AudioContext().decodeAudioData(buf))
      .then(decoded => {
        if (cancelled) return;
        const left = decoded.getChannelData(0);
        const right = decoded.numberOfChannels > 1 ? decoded.getChannelData(1) : left;
        setStandardLeftWaveform(downsampleChannel(left, barCount));
        setStandardRightWaveform(downsampleChannel(right, barCount));
      })
      .catch(() => {});
    return () => { cancelled = true; audio.pause(); audio.src = ''; };
  }, [barCount, visible]);

  useEffect(() => {
    const container = toggleContainerRef.current;
    if (!container) return;
    const measure = () => {
      const buttons = container.querySelectorAll('button');
      const idx = datasetType === 'standard' ? 0 : 1;
      const btn = buttons[idx];
      if (btn) {
        setToggleIndicator({ left: (btn as HTMLElement).offsetLeft, width: (btn as HTMLElement).offsetWidth });
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(measure));
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [datasetType]);

  // Real audio playback — plays the correct audio per tab
  useEffect(() => {
    const voqalsAudio = voqalsAudioRef.current;
    const standardAudio = standardAudioRef.current;
    if (isPlaying) {
      const activeAudio = datasetType === 'voqals' ? voqalsAudio : standardAudio;
      const inactiveAudio = datasetType === 'voqals' ? standardAudio : voqalsAudio;
      if (inactiveAudio) { inactiveAudio.pause(); }
      if (activeAudio) {
        activeAudio.play().catch(() => {});
        const tick = () => {
          if (activeAudio.duration) setPlaybackProgress(activeAudio.currentTime / activeAudio.duration);
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      }
    } else {
      voqalsAudio?.pause();
      standardAudio?.pause();
      cancelAnimationFrame(rafRef.current);
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying, datasetType]);

  const togglePlay = () => {
    const audio = datasetType === 'voqals' ? voqalsAudioRef.current : standardAudioRef.current;
    if (audio && playbackProgress >= 1) { audio.currentTime = 0; setPlaybackProgress(0); }
    setIsPlaying(!isPlaying);
  };

  // Use real waveform data per tab
  const userWaveform = datasetType === 'voqals'
    ? (voqalsLeftWaveform.length > 0 ? voqalsLeftWaveform : Array.from({ length: barCount }, () => 15))
    : (standardLeftWaveform.length > 0 ? standardLeftWaveform : Array.from({ length: barCount }, () => 15));
  const agentWaveform = datasetType === 'voqals'
    ? (voqalsRightWaveform.length > 0 ? voqalsRightWaveform : Array.from({ length: barCount }, () => 15))
    : (standardRightWaveform.length > 0 ? standardRightWaveform : Array.from({ length: barCount }, () => 15));
  return (
    <div className={`gradient-border rounded-2xl md:rounded-[clamp(20px,1.85vw,32px)] backdrop-blur-xl shadow-2xl relative h-full md:h-full`}
      style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}
    >
      <div className="px-6 py-[3dvh] md:p-[clamp(20px,1.85vw,32px)] flex flex-col h-full overflow-hidden rounded-[inherit]">
      {/* Top highlight */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      {/* Toggle Switch */}
      <div className={`flex justify-center mb-5 md:mb-[clamp(16px,1.39vw,24px)]`}>
        <div ref={toggleContainerRef} className="bg-white/5 p-1 rounded-full flex items-center relative">
          {/* Sliding indicator */}
          <motion.div
            className="absolute top-1 bottom-1 rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] shadow-[0_0_20px_rgba(27,54,255,0.3)]"
            animate={{ left: toggleIndicator.left, width: toggleIndicator.width }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          />
          <button
            onClick={() => { setDatasetType('standard'); setIsPlaying(false); setPlaybackProgress(0); }}
            className={`relative z-10 px-4 md:px-[clamp(16px,1.39vw,24px)] py-2.5 md:py-[clamp(7px,0.58vw,10px)] rounded-full text-[11px] md:text-[clamp(8px,0.64vw,11px)] font-bold tracking-[0.15em] md:tracking-[0.2em] uppercase transition-colors duration-300 whitespace-nowrap ${
              datasetType === 'standard' ? 'text-white' : 'text-white/40 hover:text-white/60'
            }`}
          >Standard Datasets</button>
          <button
            onClick={() => { setDatasetType('voqals'); setIsPlaying(false); setPlaybackProgress(0); }}
            className={`relative z-10 px-4 md:px-[clamp(16px,1.39vw,24px)] py-2.5 md:py-[clamp(7px,0.58vw,10px)] rounded-full text-[11px] md:text-[clamp(8px,0.64vw,11px)] font-bold tracking-[0.15em] md:tracking-[0.2em] uppercase transition-colors duration-300 whitespace-nowrap ${
              datasetType === 'voqals' ? 'text-white' : 'text-white/40 hover:text-white/60'
            }`}
          >Voqals</button>
        </div>
      </div>
      {/* Audio Section */}
      <div className={`mb-5 md:mb-[clamp(8px,0.93vw,16px)] flex flex-col gap-2 md:gap-[clamp(8px,0.93vw,16px)]`}>
        <div className="flex items-center justify-between mb-0.5 md:mb-[clamp(2px,0.23vw,4px)]">
          <span className={`text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold tracking-widest uppercase text-white/40 truncate mr-2`}>
            {datasetType === 'standard' ? 'Customer Support · Mono · Hindi' : 'Voq_CS_Dual_CodeSwitch_Intent_Empathy'}
          </span>
          <div className={`text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-mono text-white/30 shrink-0`}>
            {(() => { const cur = Math.floor(playbackProgress * duration); const tot = Math.floor(duration); const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`; return `${fmt(cur)} / ${fmt(tot)}`; })()}
          </div>
        </div>
        <div className={`flex items-stretch gap-4 p-3 md:gap-[clamp(16px,1.39vw,24px)] md:p-[clamp(8px,0.69vw,12px)] rounded-xl bg-white/[0.03] border border-white/[0.06] shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]`}>
          <div className="flex-1 flex flex-col gap-1.5 md:gap-[clamp(4px,0.35vw,6px)] justify-center w-full min-w-0">
            {datasetType === 'standard' ? (
              /* Standard: single mono waveform */
              <div
                className="flex-1 h-10 md:h-[clamp(20px,1.62vw,28px)] flex items-center gap-0 overflow-hidden relative min-w-0 cursor-pointer"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const pos = (e.clientX - rect.left) / rect.width;
                  const audio = standardAudioRef.current;
                  if (audio && audio.duration) { audio.currentTime = pos * audio.duration; setPlaybackProgress(pos); }
                }}
              >
                {userWaveform.map((height, i) => {
                  const barPos = i / userWaveform.length;
                  const isPassed = playbackProgress > barPos;
                  // Standard Hindi + English segments (from annotation JSON, duration 5.232s)
                  const stdSegments = [
                    { start: 0, end: 0.3270, color: '#A78BFA' },     // Mujhe khed hai. Aapka — Hindi
                    { start: 0.3270, end: 0.4539, color: '#56CFFC' }, // transaction fail — English
                    { start: 0.4539, end: 1.0, color: '#A78BFA' },   // ho gaya hai. Kripya... — Hindi
                  ];
                  const seg = stdSegments.find(s => barPos >= s.start && barPos < s.end);
                  const color = seg ? seg.color : '#888888';
                  return <div key={i} className="rounded-full transition-colors duration-75" style={{ flex: '1 1 0', minWidth: 0, height: `${height}%`, backgroundColor: isPassed ? color : `${color}33`, opacity: isPassed ? 0.8 : 0.25 }} />;
                })}
              </div>
            ) : (
              /* Voqals: dual stereo waveform */
              <>
                <div className={`flex items-center gap-3 md:gap-[clamp(8px,0.93vw,16px)]`}>
                  <span className={`text-[10px] w-10 md:text-[clamp(6px,0.52vw,9px)] font-bold tracking-widest uppercase text-[#56CFFC]/50 md:w-[clamp(28px,2.31vw,40px)] text-right shrink-0`}>User</span>
                  <div
                    className={`flex-1 h-8 md:h-[clamp(14px,1.16vw,20px)] flex items-center gap-0 overflow-hidden relative min-w-0 cursor-pointer`}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const pos = (e.clientX - rect.left) / rect.width;
                      const audio = voqalsAudioRef.current;
                      if (audio && audio.duration) { audio.currentTime = pos * audio.duration; setPlaybackProgress(pos); }
                    }}
                  >
                    {userWaveform.map((height, i) => {
                      const barPos = i / userWaveform.length;
                      const isPassed = playbackProgress > barPos;
                      const userSegments = [
                        { start: 0.0207, end: 0.0432, color: '#F59E0B' }, // Oh dada — Marathi
                        { start: 0.0432, end: 0.0575, color: '#A78BFA' }, // mera — Hindi
                        { start: 0.0575, end: 0.0877, color: '#56CFFC' }, // payment decline — English
                        { start: 0.0877, end: 0.1176, color: '#A78BFA' }, // ho gaya hai — Hindi
                        { start: 0.1176, end: 0.1755, color: '#F59E0B' }, // Ata mi kay karun? — Marathi
                        { start: 0.1755, end: 0.1823, color: '#A78BFA' }, // Mere — Hindi
                        { start: 0.1823, end: 0.1979, color: '#56CFFC' }, // account — English
                        { start: 0.1979, end: 0.2227, color: '#A78BFA' }, // se bhi paise — Hindi
                        { start: 0.2227, end: 0.2366, color: '#56CFFC' }, // deduct — English
                        { start: 0.2366, end: 0.2729, color: '#A78BFA' }, // ho gaye hain — Hindi
                        { start: 0.2729, end: 0.3117, color: '#56CFFC' }, // Please urgently check — English
                        { start: 0.3117, end: 0.3438, color: '#A78BFA' }, // karo na — Hindi
                        { start: 0.3438, end: 0.3790, color: '#56CFFC' }, // Madam — English
                        { start: 0.3790, end: 0.5072, color: '#F59E0B' }, // tumi kahi kalji karu naka. Mala dista — Marathi
                        { start: 0.5072, end: 0.5928, color: '#56CFFC' }, // that the payment has been decline — English
                        { start: 0.5928, end: 0.6814, color: '#F59E0B' }, // ani paise pan deduct zhalet — Marathi
                        { start: 0.6814, end: 0.7210, color: '#56CFFC' }, // But don't worry — English
                        { start: 0.7210, end: 0.7449, color: '#A78BFA' }, // yeh — Hindi
                        { start: 0.7449, end: 0.7706, color: '#56CFFC' }, // 24 hours — English
                        { start: 0.7706, end: 0.7772, color: '#A78BFA' }, // mein — Hindi
                        { start: 0.7772, end: 0.7916, color: '#56CFFC' }, // reverse — English
                        { start: 0.7916, end: 0.8951, color: '#A78BFA' }, // ho jaega. Main aapke liye ek — Hindi
                        { start: 0.8951, end: 0.9257, color: '#56CFFC' }, // urgent ticket raise — English
                        { start: 0.9257, end: 0.9545, color: '#A78BFA' }, // kar deta hoon — Hindi
                        { start: 0.9545, end: 0.9953, color: '#56CFFC' }, // Is that okay? — English
                      ];
                      const seg = userSegments.find(s => barPos >= s.start && barPos < s.end);
                      const color = seg ? seg.color : '#888888';
                      const isSilence = !seg;
                      return <div key={i} className="rounded-full transition-colors duration-75" style={{ flex: '1 1 0', minWidth: 0, height: isSilence ? '15%' : `${height}%`, backgroundColor: isPassed ? color : `${color}33`, opacity: isSilence ? 0.1 : (isPassed ? 1 : 0.3) }} />;
                    })}
                  </div>
                </div>
                <div className={`flex items-center gap-3 md:gap-[clamp(8px,0.93vw,16px)]`}>
                  <span className={`text-[10px] w-10 md:text-[clamp(6px,0.52vw,9px)] font-bold tracking-widest uppercase text-white/50 md:w-[clamp(28px,2.31vw,40px)] text-right shrink-0`}>Agent</span>
                  <div
                    className={`flex-1 h-8 md:h-[clamp(14px,1.16vw,20px)] flex items-center gap-0 overflow-hidden relative min-w-0 cursor-pointer`}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const pos = (e.clientX - rect.left) / rect.width;
                      const audio = voqalsAudioRef.current;
                      if (audio && audio.duration) { audio.currentTime = pos * audio.duration; setPlaybackProgress(pos); }
                    }}
                  >
                    {agentWaveform.map((height, i) => {
                      const barPos = i / agentWaveform.length;
                      const isPassed = playbackProgress > barPos;
                      const agentSegments = [
                        { start: 0.0207, end: 0.0432, color: '#F59E0B' }, // Oh dada — Marathi
                        { start: 0.0432, end: 0.0575, color: '#A78BFA' }, // mera — Hindi
                        { start: 0.0575, end: 0.0877, color: '#56CFFC' }, // payment decline — English
                        { start: 0.0877, end: 0.1176, color: '#A78BFA' }, // ho gaya hai — Hindi
                        { start: 0.1176, end: 0.1755, color: '#F59E0B' }, // Ata mi kay karun? — Marathi
                        { start: 0.1755, end: 0.1823, color: '#A78BFA' }, // Mere — Hindi
                        { start: 0.1823, end: 0.1979, color: '#56CFFC' }, // account — English
                        { start: 0.1979, end: 0.2227, color: '#A78BFA' }, // se bhi paise — Hindi
                        { start: 0.2227, end: 0.2366, color: '#56CFFC' }, // deduct — English
                        { start: 0.2366, end: 0.2729, color: '#A78BFA' }, // ho gaye hain — Hindi
                        { start: 0.2729, end: 0.3117, color: '#56CFFC' }, // Please urgently check — English
                        { start: 0.3117, end: 0.3438, color: '#A78BFA' }, // karo na — Hindi
                        { start: 0.3438, end: 0.3790, color: '#56CFFC' }, // Madam — English
                        { start: 0.3790, end: 0.5072, color: '#F59E0B' }, // tumi kahi kalji karu naka. Mala dista — Marathi
                        { start: 0.5072, end: 0.5928, color: '#56CFFC' }, // that the payment has been decline — English
                        { start: 0.5928, end: 0.6814, color: '#F59E0B' }, // ani paise pan deduct zhalet — Marathi
                        { start: 0.6814, end: 0.7210, color: '#56CFFC' }, // But don't worry — English
                        { start: 0.7210, end: 0.7449, color: '#A78BFA' }, // yeh — Hindi
                        { start: 0.7449, end: 0.7706, color: '#56CFFC' }, // 24 hours — English
                        { start: 0.7706, end: 0.7772, color: '#A78BFA' }, // mein — Hindi
                        { start: 0.7772, end: 0.7916, color: '#56CFFC' }, // reverse — English
                        { start: 0.7916, end: 0.8951, color: '#A78BFA' }, // ho jaega. Main aapke liye ek — Hindi
                        { start: 0.8951, end: 0.9257, color: '#56CFFC' }, // urgent ticket raise — English
                        { start: 0.9257, end: 0.9545, color: '#A78BFA' }, // kar deta hoon — Hindi
                        { start: 0.9545, end: 0.9953, color: '#56CFFC' }, // Is that okay? — English
                      ];
                      const seg = agentSegments.find(s => barPos >= s.start && barPos < s.end);
                      const color = seg ? seg.color : '#888888';
                      const isSilence = !seg;
                      return <div key={i} className="rounded-full transition-colors duration-75" style={{ flex: '1 1 0', minWidth: 0, height: isSilence ? '15%' : `${height}%`, backgroundColor: isPassed ? color : `${color}33`, opacity: isSilence ? 0.1 : (isPassed ? 1 : 0.3) }} />;
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
          <button aria-label={isPlaying ? "Pause audio" : "Play audio"} onClick={togglePlay} className={`w-14 aspect-square md:w-[clamp(32px,2.78vw,48px)] shrink-0 rounded-full flex items-center justify-center transition-all ${datasetType === 'voqals' ? 'bg-gradient-to-br from-[#56CFFC] to-[#1B36FF] hover:shadow-[0_0_16px_rgba(86,207,252,0.3)]' : 'bg-white/5 border border-white/10 hover:bg-white/10'}`}>
            {isPlaying ? <Pause className={`w-5 h-5 md:w-[clamp(14px,1.16vw,20px)] md:h-[clamp(14px,1.16vw,20px)] text-white fill-white`} /> : <Play className={`w-5 h-5 md:w-[clamp(14px,1.16vw,20px)] md:h-[clamp(14px,1.16vw,20px)] text-white fill-white ml-0.5 md:ml-1`} />}
          </button>
        </div>
      </div>
      <div className={`h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent mb-5 md:mb-[clamp(8px,0.93vw,16px)]`} />
      {/* Transcript + Metadata — on mobile, opacity driven by scroll progress */}
      <motion.div style={detailsOpacity != null ? { opacity: detailsOpacity, filter: detailsBlur } : undefined} className="flex flex-col">
      {/* Transcript */}
      <div className={`mb-4 md:mb-[clamp(8px,0.93vw,16px)] md:min-h-[clamp(80px,6.94vw,120px)]`}>
        <div className={`flex items-center justify-center gap-4 mb-3 text-[9px] md:gap-[clamp(8px,0.93vw,16px)] md:mb-[clamp(8px,0.93vw,16px)] md:text-[clamp(6px,0.52vw,9px)] font-bold uppercase tracking-widest`}>
          {datasetType === 'standard' ? (
            <>
              <span className="text-[#A78BFA] flex items-center gap-1.5 md:gap-[clamp(4px,0.35vw,8px)]"><div className={`w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-[#A78BFA]`} /> Hindi</span>
              <span className="text-[#56CFFC] flex items-center gap-1.5 md:gap-[clamp(4px,0.35vw,8px)]"><div className={`w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-[#56CFFC]`} /> English</span>
            </>
          ) : (
            <>
              <span className="text-[#F59E0B] flex items-center gap-1.5 md:gap-[clamp(4px,0.35vw,8px)]"><div className={`w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-[#F59E0B]`} /> Marathi</span>
              <span className="text-[#A78BFA] flex items-center gap-1.5 md:gap-[clamp(4px,0.35vw,8px)]"><div className={`w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-[#A78BFA]`} /> Hindi</span>
              <span className="text-[#56CFFC] flex items-center gap-1.5 md:gap-[clamp(4px,0.35vw,8px)]"><div className={`w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-[#56CFFC]`} /> English</span>
            </>
          )}
        </div>
        <div className={`flex flex-col gap-3 md:gap-[clamp(8px,0.93vw,16px)]`}>
          <div className={`flex gap-3 md:gap-[clamp(16px,1.39vw,24px)]`}>
            <div className={`w-10 md:w-[clamp(28px,2.31vw,40px)] shrink-0 flex flex-col items-end`}>
              <span className={`text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold tracking-widest uppercase text-white/30 mt-0.5 md:mt-[clamp(2px,0.23vw,4px)]`}>User</span>
            </div>
            <div className={`flex-1 text-[14px] leading-relaxed md:text-[clamp(11px,0.93vw,16px)] md:leading-relaxed font-medium`}>
              {datasetType === 'standard' ? (
                <span className="text-white/40">—</span>
              ) : (
                <span className="opacity-90">
                  <span className="text-[#F59E0B]">Oh dada</span>, <span className="text-[#A78BFA]">mera</span> <span className="text-[#56CFFC]">payment decline</span> <span className="text-[#A78BFA]">ho gaya hai.</span> <span className="text-[#F59E0B]">Ata mi kay karun?</span> <span className="text-[#A78BFA]">Mere</span> <span className="text-[#56CFFC]">account</span> <span className="text-[#A78BFA]">se bhi paise</span> <span className="text-[#56CFFC]">deduct</span> <span className="text-[#A78BFA]">ho gaye hain.</span> <span className="text-[#56CFFC]">Please urgently check</span> <span className="text-[#A78BFA]">karo na.</span>
                </span>
              )}
            </div>
          </div>
          <div className={`flex gap-3 md:gap-[clamp(16px,1.39vw,24px)]`}>
            <div className={`w-10 md:w-[clamp(28px,2.31vw,40px)] shrink-0 flex flex-col items-end`}>
              <span className={`text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold tracking-widest uppercase text-white/30 mt-0.5 md:mt-[clamp(2px,0.23vw,4px)]`}>Agent</span>
            </div>
            <div className={`flex-1 text-[14px] leading-relaxed md:text-[clamp(11px,0.93vw,16px)] md:leading-relaxed font-medium`}>
              {datasetType === 'standard' ? (
                <span className="text-white/60"><span className="text-[#A78BFA]/60">Mujhe khed hai. Aapka</span> <span className="text-[#56CFFC]/60">transaction fail</span> <span className="text-[#A78BFA]/60">ho gaya hai. Kripya chaubis ghante pratiksha karein.</span></span>
              ) : (
                <span className="opacity-90">
                  <span className="text-[#56CFFC]">Madam,</span> <span className="text-[#F59E0B]">tumi kahi kalji karu naka. Mala distay</span> <span className="text-[#56CFFC]">that the payment has been declined</span> <span className="text-[#F59E0B]">ani paise pan</span> <span className="text-[#56CFFC]">deduct</span> <span className="text-[#F59E0B]">zhalet.</span> <span className="text-[#56CFFC]">But don't worry,</span> <span className="text-[#A78BFA]">yeh</span> <span className="text-[#56CFFC]">24 hours</span> <span className="text-[#A78BFA]">mein</span> <span className="text-[#56CFFC]">reverse</span> <span className="text-[#A78BFA]">ho jaega. Main aapke liye ek</span> <span className="text-[#56CFFC]">urgent ticket raise</span> <span className="text-[#A78BFA]">kar deta hoon.</span> <span className="text-[#56CFFC]">Is that okay?</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Metadata */}
      <div className={`pt-4 md:pt-[clamp(8px,0.93vw,16px)] border-t border-white/5`}>
        {datasetType === 'standard' ? (
          <div className={`flex flex-wrap justify-center gap-3 md:gap-[clamp(8px,0.93vw,16px)]`}>
            <div className={`px-4 py-1.5 text-[10px] md:px-[clamp(8px,0.93vw,16px)] md:py-[clamp(4px,0.35vw,6px)] rounded-full bg-white/5 md:text-[clamp(7px,0.58vw,10px)] font-medium tracking-wide text-white/40 uppercase`}>Monolingual</div>
            <div className={`px-4 py-1.5 text-[10px] md:px-[clamp(8px,0.93vw,16px)] md:py-[clamp(4px,0.35vw,6px)] rounded-full bg-white/5 md:text-[clamp(7px,0.58vw,10px)] font-medium tracking-wide text-white/40 uppercase`}>Rigid Syntax</div>
            <div className={`px-4 py-1.5 text-[10px] md:px-[clamp(8px,0.93vw,16px)] md:py-[clamp(4px,0.35vw,6px)] rounded-full bg-white/5 md:text-[clamp(7px,0.58vw,10px)] font-medium tracking-wide text-white/40 uppercase`}>Zero Empathy</div>
          </div>
        ) : (
          <div className={`flex flex-col gap-2 md:gap-[clamp(6px,0.52vw,12px)]`}>
            <div className={`flex items-start md:items-center gap-3 md:gap-[clamp(8px,0.93vw,16px)] text-[11px] md:text-[clamp(8px,0.64vw,11px)]`}>
              <span className={`font-bold text-white/30 uppercase tracking-widest w-10 md:w-[clamp(28px,2.31vw,40px)] text-right shrink-0`}>User</span>
              <div className={`flex flex-wrap gap-x-3 md:gap-x-[clamp(6px,0.52vw,12px)] gap-y-1`}>
                <span className="text-white/60"><span className="text-white/30 mr-1">Persona:</span>GenPop, 34, Pune</span>
                <span className={`inline md:inline text-white/10`}>•</span>
                <span className="text-white/60"><span className="text-white/30 mr-1">Intent:</span>Complain & resolution</span>
                <span className={`inline md:inline text-white/10`}>•</span>
                <span className="text-[#FF56A5]/80"><span className="text-white/30 mr-1">Emotion:</span>Frustrated & angry</span>
              </div>
            </div>
            <div className={`flex items-start md:items-center gap-3 md:gap-[clamp(8px,0.93vw,16px)] text-[11px] md:text-[clamp(8px,0.64vw,11px)]`}>
              <span className={`font-bold text-white/30 uppercase tracking-widest w-10 md:w-[clamp(28px,2.31vw,40px)] text-right shrink-0`}>Agent</span>
              <div className={`flex flex-wrap gap-x-3 md:gap-x-[clamp(6px,0.52vw,12px)] gap-y-1`}>
                <span className="text-white/60"><span className="text-white/30 mr-1">Persona:</span>Senior Support Rep</span>
                <span className={`inline md:inline text-white/10`}>•</span>
                <span className="text-white/60"><span className="text-white/30 mr-1">Intent:</span>De-escalation</span>
                <span className={`inline md:inline text-white/10`}>•</span>
                <span className="text-[#56CFFC]/80"><span className="text-white/30 mr-1">Emotion:</span>Empathetic & reassuring</span>
                <span className={`inline md:inline text-white/10`}>•</span>
                <span className="text-[#50E3C2]/80"><span className="text-white/30 mr-1">Action:</span>Language Mirroring</span>
              </div>
            </div>
          </div>
        )}
      </div>
      </motion.div>
      </div>
    </div>
  );
};
/* ── USP1: Desktop layout (unchanged — heading + full widget side by side) ── */
const Usp1Complexity = () => {
  const [datasetType, setDatasetType] = useState<'standard' | 'voqals'>('voqals');
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={sectionRef} className="max-w-[calc(var(--max-content)-2*var(--px-container))] w-full mx-auto grid grid-cols-1 md:grid-cols-12 gap-[var(--gap-medium)] items-center pointer-events-auto">
      {/* Left Column */}
      <div className="md:col-span-5 flex flex-col justify-center">
        <h2 className="font-display text-[length:var(--heading-usp)] md:text-[clamp(36px,3.13vw,54px)] font-black mb-[clamp(8px,0.93vw,16px)] text-white leading-[1.05] tracking-tighter uppercase overflow-visible">
          REAL&#8209;WORLD<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic whitespace-nowrap pr-[0.15em]">CODE&#8209;SWITCHING.</span>
        </h2>
        <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed mb-[clamp(16px,1.39vw,24px)] max-w-lg">
          In real Indian conversations, people move fluidly between 2 or 3 languages within a single breath. Every Voqals dataset is built around this reality, capturing natural, complex code-switched speech so your model understands how India actually communicates.
        </p>
      </div>
      {/* Right Column */}
      <div className="md:col-span-7 relative">
        <Usp1ExpandedWidget datasetType={datasetType} setDatasetType={setDatasetType} isPlaying={isPlaying} setIsPlaying={setIsPlaying} playbackProgress={playbackProgress} setPlaybackProgress={setPlaybackProgress} visible={visible} />
      </div>
    </div>
  );
};
/* ── USP1: Mobile wrapper — single widget, scroll-driven peek → fullscreen ── */
const Usp1MobileWrapper = () => {
  const [datasetType, setDatasetType] = useState<'standard' | 'voqals'>('voqals');
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const widgetProps = { datasetType, setDatasetType, isPlaying, setIsPlaying, playbackProgress, setPlaybackProgress, visible };
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollYProgress = useMotionValue(0);
  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    const section = sectionRef.current;
    if (!container || !section) return;
    const handleScroll = () => {
      // Walk up offsetParent chain to get absolute position within scroll container
      let sectionTop = 0;
      let el: HTMLElement | null = section;
      while (el && el !== container) {
        sectionTop += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
      }
      const sectionHeight = section.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      // progress 0 = section top at viewport top, progress 1 = section bottom at viewport bottom
      const start = sectionTop;
      const end = sectionTop + sectionHeight - containerHeight;
      const progress = Math.max(0, Math.min(1, (scrollTop - start) / (end - start)));
      scrollYProgress.set(progress);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  // Lazy-load audio when section enters viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  // ── Scroll-driven animation timeline ──
  // 0.0–0.3: Heading fades out, widget starts growing
  // 0.3–0.35: isExpanded triggers (masked by blur)
  // 0.35–0.65: Margins/radius shrink, blur clears, content revealed
  // 0.65–0.8: Widget finishes growing to full height
  const headingOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const widgetBorderRadius = useTransform(scrollYProgress, [0.1, 0.6], [24, 16]);
  const widgetMarginLR = useTransform(scrollYProgress, [0, 0.35], [24, 0]);
  const widgetMarginB = useTransform(scrollYProgress, [0.1, 0.6], [16, 0]);
  const widgetHeight = useTransform(scrollYProgress, (v) => {
    const p = Math.min(1, Math.max(0, v / 0.8)); // normalize to 0-1 over 0–0.8 range
    const vh = window.innerHeight;
    const startH = vh * 0.38;
    const endH = vh - 90;
    return startH + (endH - startH) * p;
  });
  const detailsOpacity = useTransform(scrollYProgress, [0.2, 0.65], [0.7, 1]);
  const detailsBlurAmount = useTransform(scrollYProgress, [0.2, 0.65], [4, 0]);
  const detailsBlurFilter = useMotionTemplate`blur(${detailsBlurAmount}px)`;
  // isExpanded fires at 0.35 while blur is still ~4px, masking the class size jump
  const [isExpanded, setIsExpanded] = useState(false);
  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    const expanded = v > 0.35;
    if (expanded !== isExpanded) setIsExpanded(expanded);
  });
  return (
    <div ref={sectionRef} id="usp1-mobile" className="md:hidden h-[200dvh] w-full relative shrink-0">
      <div className="sticky top-0 h-[100dvh] w-full flex flex-col overflow-hidden">
        {/* Heading — vertically centered in the viewport, fades out on scroll */}
        <motion.div
          style={{ opacity: headingOpacity }}
          className="flex-1 flex flex-col items-center justify-center text-center px-[var(--px-container)] overflow-hidden pointer-events-auto pt-[70px]"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)] mb-6">
            Real-World Code-Switching
          </div>
          <h2 className="font-display text-[length:var(--heading-usp)] font-black mb-6 text-[#EEEEEE] leading-[1.05] tracking-tighter uppercase overflow-visible">
            REAL&#8209;WORLD<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic whitespace-nowrap pr-[0.15em]">CODE&#8209;SWITCHING.</span>
          </h2>
          <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed max-w-sm mx-auto">
            In real Indian conversations, people move fluidly between 2 or 3 languages within a single breath. We capture natural, complex code-switched speech so your model understands how India actually communicates.
          </p>
        </motion.div>
        {/* Widget — fixed to bottom, explicit height grows from peek to fullscreen */}
        <motion.div
          style={{ borderRadius: widgetBorderRadius, marginLeft: widgetMarginLR, marginRight: widgetMarginLR, marginBottom: widgetMarginB, height: widgetHeight }}
          className="gradient-border shrink-0 pointer-events-auto"
        >
          <div className="w-full h-full overflow-hidden" style={{ borderRadius: 'inherit' }}>
            <Usp1ExpandedWidget {...widgetProps} detailsOpacity={detailsOpacity} detailsBlur={detailsBlurFilter} isExpanded={isExpanded} />
          </div>
        </motion.div>
      </div>
    </div>
  );
};
const Usp2Technicality = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sliderPos, setSliderPos] = useState(50);
  const [hasInteracted, setHasInteracted] = useState(false);
  const autoAnimRef = useRef<number>(0);
  const autoTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Auto-slide animation: sweeps once, then repeats every 5s until user interacts
  useEffect(() => {
    if (hasInteracted) {
      cancelAnimationFrame(autoAnimRef.current);
      clearTimeout(autoTimerRef.current);
      return;
    }
    const runSweep = () => {
      let start: number | null = null;
      const duration = 2400; // ms for one full sweep
      const animate = (ts: number) => {
        if (!start) start = ts;
        const elapsed = ts - start;
        const t = Math.min(elapsed / duration, 1);
        // Ease in-out: go from 50 → 5 → 95 → 50
        let pos: number;
        if (t < 0.33) {
          const p = t / 0.33;
          pos = 50 - 45 * (1 - Math.pow(1 - p, 3)); // 50 → 5
        } else if (t < 0.66) {
          const p = (t - 0.33) / 0.33;
          pos = 5 + 90 * (1 - Math.pow(1 - p, 3)); // 5 → 95
        } else {
          const p = (t - 0.66) / 0.34;
          pos = 95 - 45 * (1 - Math.pow(1 - p, 3)); // 95 → 50
        }
        setSliderPos(pos);
        if (t < 1) {
          autoAnimRef.current = requestAnimationFrame(animate);
        } else {
          // Schedule next sweep
          autoTimerRef.current = setTimeout(runSweep, 5000);
        }
      };
      autoAnimRef.current = requestAnimationFrame(animate);
    };
    // Initial delay before first sweep
    autoTimerRef.current = setTimeout(runSweep, 1500);
    return () => {
      cancelAnimationFrame(autoAnimRef.current);
      clearTimeout(autoTimerRef.current);
    };
  }, [hasInteracted]);
  return (
    <>
      <div className="max-w-[calc(var(--max-content)-2*var(--px-container))] w-full mx-auto grid grid-cols-1 md:grid-cols-12 gap-[var(--gap-medium)] items-center pointer-events-auto">
        {/* Left Column: Copy */}
        <div className="md:col-span-5 flex flex-col justify-center order-1">
          <h3 className="font-display text-[length:var(--heading-usp)] md:text-[clamp(36px,3.13vw,54px)] font-black mb-[clamp(8px,0.93vw,16px)] text-white leading-[1.1] tracking-tighter uppercase relative z-10 overflow-visible">
            <span className="whitespace-nowrap">TRAIN ON SPEECH.</span> <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic pr-[0.15em]">NOT NOISE.</span>
          </h3>
          <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed mb-[clamp(12px,0.93vw,16px)] max-w-lg relative z-10">
            The <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-semibold">Voqals Quality Standard</span> is our proprietary studio certification process refined over years of AI speech data production. Every studio is audited, modified, and certified to our specifications so the only thing your model learns from is human voice.
          </p>
          {/* Studio Specifications */}
          <div className="flex flex-col gap-[clamp(8px,0.69vw,12px)] mb-[clamp(16px,1.39vw,24px)] relative z-10">
            <div className="flex items-center gap-[clamp(10px,0.81vw,14px)]">
              <div className="w-[clamp(6px,0.52vw,9px)] h-[clamp(6px,0.52vw,9px)] rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] shrink-0 shadow-[0_0_8px_rgba(86,207,252,0.4)]" />
              <span className="text-white text-[length:var(--text-body)] font-semibold tracking-wide">Below {'\u2212'}60dB noise floor</span>
            </div>
            <div className="flex items-center gap-[clamp(10px,0.81vw,14px)]">
              <div className="w-[clamp(6px,0.52vw,9px)] h-[clamp(6px,0.52vw,9px)] rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] shrink-0 shadow-[0_0_8px_rgba(86,207,252,0.4)]" />
              <span className="text-white text-[length:var(--text-body)] font-semibold tracking-wide">Under 200ms RT60</span>
            </div>
            <div className="flex items-center gap-[clamp(10px,0.81vw,14px)]">
              <div className="w-[clamp(6px,0.52vw,9px)] h-[clamp(6px,0.52vw,9px)] rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] shrink-0 shadow-[0_0_8px_rgba(86,207,252,0.4)]" />
              <span className="text-white text-[length:var(--text-body)] font-semibold tracking-wide">Zero electrical interference</span>
            </div>
          </div>
        </div>
        {/* Right Column: Interactive Slider */}
        <div className="md:col-span-7 relative order-2 flex flex-col">
          <div className="gradient-border rounded-[clamp(20px,1.85vw,32px)] backdrop-blur-xl p-[clamp(16px,1.4vw,24px)] flex flex-col shadow-2xl relative"
            style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}
          >
            {/* Widget Title */}
            <div className="flex flex-col items-center text-center mb-[clamp(6px,0.52vw,12px)]">
              <h4 className="text-[clamp(7px,0.58vw,10px)] font-bold uppercase tracking-[0.2em] text-white/60 mb-[clamp(2px,0.23vw,4px)]">The Voqals Certified Studio Difference</h4>
              <div className="flex items-center gap-[clamp(4px,0.46vw,8px)] text-[clamp(6px,0.52vw,9px)] uppercase tracking-widest text-white/30">
                <span>Slide to compare</span>
                <motion.div animate={{ x: [0, 4, 0] }} transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}>
                  <ArrowRight className="w-[clamp(8px,0.69vw,12px)] h-[clamp(8px,0.69vw,12px)]" />
                </motion.div>
              </div>
            </div>
          <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden select-none group border border-white/10 shadow-[inset_0_0_40px_rgba(86,207,252,0.04)] bg-[#0A0A0A]">

            {/* After Image (Background) - Voqals */}
            <div className="absolute inset-0 bg-black flex items-center justify-center">
              <img
                src="/clean-audio.webp"
                alt="Clean Spectrogram"
                loading="lazy"
                width="1920"
                height="1080"
                className="absolute inset-0 w-full h-full object-contain opacity-90"
              />
              <div
                className="absolute inset-0 z-10"
                style={{ clipPath: `polygon(${sliderPos}% 0, 100% 0, 100% 100%, ${sliderPos}% 100%)` }}
              >
                <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md px-[clamp(8px,0.93vw,16px)] py-[clamp(4px,0.46vw,8px)] rounded-full text-[clamp(7px,0.58vw,10px)] font-bold border border-[#56CFFC]/30 uppercase tracking-widest shadow-lg">
                  Voqals Dataset
                </div>

              </div>
            </div>
            {/* Before Image (Foreground) - Standard */}
            <div
              className="absolute inset-0 bg-black flex items-center justify-center border-r border-white/20"
              style={{ clipPath: `polygon(0 0, ${sliderPos}% 0, ${sliderPos}% 100%, 0 100%)` }}
            >
              <img
                src="/bad-audio.webp"
                alt="Corrupted Spectrogram"
                loading="lazy"
                width="1920"
                height="1080"
                className="absolute inset-0 w-full h-full object-contain opacity-90"
              />
              <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-md px-[clamp(8px,0.93vw,16px)] py-[clamp(4px,0.46vw,8px)] rounded-full text-[clamp(7px,0.58vw,10px)] font-bold text-yellow-500 border border-yellow-500/30 uppercase tracking-widest z-10 shadow-lg">
                Standard Dataset
              </div>
              
            </div>
            {/* Slider Handle — minimal line with small diamond grip */}
            <div
              className="absolute top-0 bottom-0 w-px bg-white/70 cursor-ew-resize flex items-center justify-center pointer-events-none z-20"
              style={{ left: `${sliderPos}%` }}
            >
              <div className="w-[clamp(14px,1.16vw,20px)] h-[clamp(14px,1.16vw,20px)] bg-[#0a0a0a] border border-white/50 rotate-45 flex items-center justify-center shadow-[0_0_12px_rgba(255,255,255,0.15)]" />
            </div>
            {/* Invisible Range Input */}
            <input
              type="range"
              min="0"
              max="100"
              value={sliderPos}
              aria-label="Quality comparison slider"
              onChange={(e) => {
                setSliderPos(Number(e.target.value));
                if (!hasInteracted) setHasInteracted(true);
              }}
              className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-30"
            />
          </div>
            {/* Deep Dive CTA — below spectrogram */}
            <div className="flex justify-center mt-[clamp(8px,0.93vw,16px)]">
              <button
                onClick={() => setIsModalOpen(true)}
                className="group relative px-[clamp(20px,1.85vw,32px)] py-[clamp(8px,0.81vw,14px)] rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all shadow-[0_0_20px_rgba(27,54,255,0.3)] hover:shadow-[0_0_30px_rgba(86,207,252,0.4)] hover:scale-105 flex items-center justify-center"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                <span className="relative z-10 flex items-center gap-3">
                  <Play className="w-4 h-4 text-white fill-white group-hover:scale-110 transition-transform" />
                  Deep Dive
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Lightbox Modal */}
      {isModalOpen && (
        <React.Suspense fallback={null}>
          <DiagnosticModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </React.Suspense>
      )}
    </>
  );
};
/* ── USP2: Mobile wrapper — scroll-driven peek → fullscreen ── */
const Usp2MobileWrapper = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sliderPos, setSliderPos] = useState(50);
  const [hasInteracted, setHasInteracted] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const autoAnimRef = useRef<number>(0);
  const autoTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (hasInteracted) {
      cancelAnimationFrame(autoAnimRef.current);
      clearTimeout(autoTimerRef.current);
      return;
    }
    const runSweep = () => {
      let start: number | null = null;
      const duration = 2400;
      const animate = (ts: number) => {
        if (!start) start = ts;
        const elapsed = ts - start;
        const t = Math.min(elapsed / duration, 1);
        let pos: number;
        if (t < 0.33) {
          const p = t / 0.33;
          pos = 50 - 45 * (1 - Math.pow(1 - p, 3));
        } else if (t < 0.66) {
          const p = (t - 0.33) / 0.33;
          pos = 5 + 90 * (1 - Math.pow(1 - p, 3));
        } else {
          const p = (t - 0.66) / 0.34;
          pos = 95 - 45 * (1 - Math.pow(1 - p, 3));
        }
        setSliderPos(pos);
        if (t < 1) {
          autoAnimRef.current = requestAnimationFrame(animate);
        } else {
          autoTimerRef.current = setTimeout(runSweep, 5000);
        }
      };
      autoAnimRef.current = requestAnimationFrame(animate);
    };
    autoTimerRef.current = setTimeout(runSweep, 1500);
    return () => {
      cancelAnimationFrame(autoAnimRef.current);
      clearTimeout(autoTimerRef.current);
    };
  }, [hasInteracted]);
  const scrollYProgress = useMotionValue(0);
  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    const section = sectionRef.current;
    if (!container || !section) return;
    const handleScroll = () => {
      let sectionTop = 0;
      let el: HTMLElement | null = section;
      while (el && el !== container) {
        sectionTop += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
      }
      const sectionHeight = section.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      const start = sectionTop;
      const end = sectionTop + sectionHeight - containerHeight;
      const progress = Math.max(0, Math.min(1, (scrollTop - start) / (end - start)));
      scrollYProgress.set(progress);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  // ── Scroll-driven animation timeline (same as USP1) ──
  const headingOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const widgetBorderRadius = useTransform(scrollYProgress, [0.1, 0.6], [24, 16]);
  const widgetMarginLR = useTransform(scrollYProgress, [0, 0.35], [24, 0]);
  const widgetMarginB = useTransform(scrollYProgress, [0, 0.35], [16, 0]);
  const widgetHeight = useTransform(scrollYProgress, (v) => {
    const p = Math.min(1, Math.max(0, v / 0.8));
    const vh = window.innerHeight;
    const startH = vh * 0.38;
    const endH = vh - 90;
    return startH + (endH - startH) * p;
  });
  const spectrogramPadding = useTransform(scrollYProgress, [0.3, 0.7], [0, 10]);
  const deepDiveOpacity = useTransform(scrollYProgress, [0.5, 0.75], [0, 1]);
  const deepDiveHeight = useTransform(scrollYProgress, [0.5, 0.8], [0, 64]);
  return (
    <>
    <div ref={sectionRef} id="usp2-mobile" className="md:hidden h-[200dvh] w-full relative shrink-0">
      <div className="sticky top-0 h-[100dvh] w-full flex flex-col overflow-hidden">
        {/* Heading — vertically centered, fades out on scroll */}
        <motion.div
          style={{ opacity: headingOpacity }}
          className="flex-1 flex flex-col items-center justify-center text-center px-[var(--px-container)] overflow-hidden pointer-events-auto pt-[70px]"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)] mb-6">
            The Voqals Quality Standard
          </div>
          <h3 className="font-display text-[length:var(--heading-usp)] font-black mb-6 text-[#EEEEEE] leading-[1.05] tracking-tighter uppercase overflow-visible">
            <span className="whitespace-nowrap">TRAIN ON SPEECH.</span><br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic pr-[0.15em]">NOT NOISE.</span>
          </h3>
          <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed max-w-sm mx-auto">
            The <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-semibold">Voqals Quality Standard</span> is our proprietary studio certification process refined over years of AI speech data production. Every studio is audited, modified, and certified to our specifications so the only thing your model learns from is human voice.
          </p>
        </motion.div>
        {/* Widget — peek to fullscreen */}
        <motion.div
          style={{ borderRadius: widgetBorderRadius, marginLeft: widgetMarginLR, marginRight: widgetMarginLR, marginBottom: widgetMarginB, height: widgetHeight }}
          className="gradient-border shrink-0 pointer-events-auto shadow-2xl backdrop-blur-xl overflow-hidden"
        >
          <div className="w-full h-full flex flex-col relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}
          >
            {/* Top highlight */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            {/* Title */}
            <div className="flex flex-col items-center gap-2 mb-2 shrink-0 px-5 pt-[2dvh]">
              <p className="text-[10px] font-bold text-white/60 uppercase tracking-[0.2em] text-center">The Voqals Certified Studio Difference</p>
              <div className="flex items-center gap-3 w-full">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-white/8" />
                <p className="text-[8px] text-white/30 uppercase tracking-[0.25em] font-medium whitespace-nowrap">Slide to compare</p>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent to-white/8" />
              </div>
            </div>
            {/* Spectrogram slider — fills remaining space */}
            <motion.div style={{ marginLeft: spectrogramPadding, marginRight: spectrogramPadding, marginBottom: spectrogramPadding, borderRadius: spectrogramPadding }} className="flex-1 relative overflow-hidden shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)] border border-white/[0.06] bg-white/[0.03]">
              {/* After Image (Background) - Voqals */}
              <div className="absolute inset-0 bg-black">
                <img
                  src="/clean-audio.webp"
                  alt="Clean Spectrogram"
                  loading="lazy"
                  width="1920"
                  height="1080"
                  className="absolute inset-0 w-full h-full object-contain opacity-90"
                />
                <div
                  className="absolute inset-0 z-10"
                  style={{ clipPath: `polygon(${sliderPos}% 0, 100% 0, 100% 100%, ${sliderPos}% 100%)` }}
                >
                  <div className="absolute top-2.5 right-2.5 z-10 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm text-[8px] font-bold text-[#56CFFC]/80 uppercase tracking-widest border border-[#56CFFC]/15">
                    Voqals Studio
                  </div>
                </div>
              </div>
              {/* Before Image (Foreground) - Standard */}
              <div
                className="absolute inset-0 bg-black border-r border-white/10"
                style={{ clipPath: `polygon(0 0, ${sliderPos}% 0, ${sliderPos}% 100%, 0 100%)` }}
              >
                <img
                  src="/bad-audio.webp"
                  alt="Corrupted Spectrogram"
                  loading="lazy"
                  width="1920"
                  height="1080"
                  className="absolute inset-0 w-full h-full object-contain opacity-90"
                />
                <div className="absolute top-2.5 left-2.5 z-10 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm text-[8px] font-bold text-yellow-500/80 uppercase tracking-widest border border-yellow-500/15">
                  Standard Studio
                </div>
              </div>
              {/* Slider Handle — minimal line with small diamond grip */}
              <div
                className="absolute top-0 bottom-0 w-px bg-white/70 cursor-ew-resize flex items-center justify-center pointer-events-none z-20"
                style={{ left: `${sliderPos}%` }}
              >
                <div className="w-3 h-3 bg-[#0a0a0a] border border-white/50 rotate-45 flex items-center justify-center shadow-[0_0_12px_rgba(255,255,255,0.15)]" />
              </div>
              {/* Invisible Range Input */}
              <input
                type="range"
                min="0"
                max="100"
                value={sliderPos}
                aria-label="Quality comparison slider"
                onChange={(e) => {
                  setSliderPos(Number(e.target.value));
                  if (!hasInteracted) setHasInteracted(true);
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-30"
              />
            </motion.div>
            {/* Deep Dive CTA — grows into view when fullscreen */}
            <motion.div style={{ opacity: deepDiveOpacity, height: deepDiveHeight }} className="shrink-0 flex items-center justify-center overflow-hidden">
              <button
                onClick={() => setIsModalOpen(true)}
                className="group relative px-7 py-2.5 rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white text-[10px] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all shadow-[0_0_20px_rgba(27,54,255,0.3)] hover:shadow-[0_0_30px_rgba(86,207,252,0.4)] hover:scale-105 flex items-center justify-center"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <Play className="w-3.5 h-3.5 text-white fill-white" />
                  Deep Dive
                </span>
              </button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
    {/* Lightbox Modal */}
    {isModalOpen && (
      <React.Suspense fallback={null}>
        <DiagnosticModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </React.Suspense>
    )}
    </>
  );
};
/* ── USP3: Expanded Widget (shared by mobile wrapper + desktop layout) ── */
interface Usp3WidgetProps {
  activeTab: 'neutral' | 'empathetic' | 'urgent';
  handleTabChange: (tab: 'neutral' | 'empathetic' | 'urgent') => void;
  isPlaying: boolean;
  togglePlay: () => void;
  playbackProgress: number;
  duration: number;
  waveform: number[];
  detailsOpacity?: any;
  detailsBlur?: any;
  isExpanded?: boolean;
}
const Usp3ExpandedWidget = ({ activeTab, handleTabChange, isPlaying, togglePlay, playbackProgress, duration, waveform, detailsOpacity, detailsBlur, isExpanded }: Usp3WidgetProps) => {
  const tabs = ['neutral', 'empathetic', 'urgent'] as const;
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  useEffect(() => {
    const container = tabContainerRef.current;
    if (!container) return;
    const measure = () => {
      const buttons = container.querySelectorAll('button');
      const tabIndex = tabs.indexOf(activeTab);
      const btn = buttons[tabIndex];
      if (btn) {
        setIndicatorStyle({ left: (btn as HTMLElement).offsetLeft, width: (btn as HTMLElement).offsetWidth });
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(measure));
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [activeTab]);
  return (
    <div className="gradient-border rounded-2xl md:rounded-[clamp(20px,1.85vw,32px)] backdrop-blur-xl shadow-2xl relative h-full"
      style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}
    >
      <div className="px-6 py-[3dvh] md:p-[clamp(20px,1.85vw,32px)] flex flex-col h-full overflow-hidden rounded-[inherit]">
      {/* Top highlight */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      {/* Widget Heading */}
      <div className="flex flex-col items-center gap-2 md:gap-[clamp(5px,0.46vw,8px)] mb-2 md:mb-[clamp(5px,0.46vw,8px)] shrink-0">
        <p className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold text-white/60 uppercase tracking-[0.2em] text-center">The Director Difference</p>
      </div>
      {/* Toggle Switch */}
      <div className="flex justify-center mb-5 md:mb-[clamp(16px,1.39vw,24px)]">
        <div ref={tabContainerRef} className="bg-white/5 p-1 md:p-[clamp(3px,0.23vw,4px)] rounded-full flex items-center relative">
          {/* Sliding gradient indicator */}
          <motion.div
            className="absolute top-1 bottom-1 rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] shadow-[0_0_20px_rgba(27,54,255,0.3)]"
            animate={{ left: indicatorStyle.left, width: indicatorStyle.width }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          />
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`relative z-10 px-4 md:px-[clamp(16px,1.39vw,24px)] py-2.5 md:py-[clamp(7px,0.58vw,10px)] rounded-full text-[11px] md:text-[clamp(7px,0.64vw,11px)] font-bold tracking-[0.15em] md:tracking-[0.2em] uppercase transition-colors duration-300 whitespace-nowrap ${
                activeTab === tab ? 'text-white' : 'text-white/40 hover:text-white/60'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {/* Audio Player */}
      <div className="mb-5 md:mb-[clamp(14px,1.16vw,20px)] flex items-center gap-3 md:gap-[clamp(11px,0.93vw,16px)] bg-white/5 rounded-2xl md:rounded-[clamp(11px,0.93vw,16px)] p-3 md:p-[clamp(8px,0.69vw,12px)] border border-white/10">
        <div className="flex-1 flex flex-col gap-2 md:gap-[clamp(5px,0.46vw,8px)] min-w-0">
          <div className="flex justify-between items-center">
            <span className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold tracking-widest uppercase text-white/50">
              {activeTab === 'neutral' ? 'Directed: Neutral' :
               activeTab === 'empathetic' ? 'Directed: Empathy' :
               'Directed: Urgency'}
            </span>
            <span className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-mono text-white/30">
              {(() => { const cur = Math.floor(playbackProgress * duration / 1000); const tot = Math.floor(duration / 1000); const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; return `${fmt(cur)} / ${fmt(tot)}`; })()}
            </span>
          </div>
          <div className="h-8 md:h-[clamp(20px,2.3vw,40px)] flex items-end gap-[1px] overflow-hidden relative w-full">
            {waveform.map((height, i) => {
              const barProgress = i / waveform.length;
              const isPassed = playbackProgress > barProgress;
              const distBehindHead = playbackProgress - barProgress;
              const isGlow = isPassed && distBehindHead < 0.04 && playbackProgress > 0;
              return (
                <div
                  key={i}
                  className={`flex-1 min-w-0 rounded-full transition-colors duration-100 ${isPassed ? 'bg-[#56CFFC]' : 'bg-[#56CFFC]/20'}`}
                  style={{
                    height: `${height}%`,
                    boxShadow: isGlow ? `0 0 ${8 - distBehindHead * 150}px rgba(86,207,252,0.9)` : undefined,
                  }}
                />
              );
            })}
          </div>
        </div>
        <button
          aria-label={isPlaying ? "Pause audio" : "Play audio"}
          onClick={togglePlay}
          className="w-12 h-12 md:w-[clamp(32px,2.78vw,48px)] md:h-[clamp(32px,2.78vw,48px)] shrink-0 rounded-full flex items-center justify-center bg-gradient-to-br from-[#56CFFC] to-[#1B36FF] text-white hover:shadow-[0_0_16px_rgba(86,207,252,0.3)] transition-shadow"
        >
          {isPlaying ? (
            <Pause className="w-5 h-5 md:w-[clamp(14px,1.16vw,20px)] md:h-[clamp(14px,1.16vw,20px)] fill-current" />
          ) : (
            <Play className="w-5 h-5 md:w-[clamp(14px,1.16vw,20px)] md:h-[clamp(14px,1.16vw,20px)] fill-current ml-0.5" />
          )}
        </button>
      </div>
      {/* Transcript + Metadata — on mobile, opacity driven by scroll progress */}
      <motion.div style={detailsOpacity != null ? { opacity: detailsOpacity, filter: detailsBlur } : undefined} className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* Transcript Section */}
      <div className="min-h-[100px] md:min-h-[clamp(68px,5.79vw,100px)] bg-black/40 rounded-3xl md:rounded-[clamp(16px,1.39vw,24px)] p-5 md:p-[clamp(16px,1.39vw,24px)] border border-white/5 relative mb-4 md:mb-[clamp(11px,0.93vw,16px)]">
        <AnimatePresence mode="wait">
          {activeTab === 'neutral' && (
            <motion.div
              key="neutral"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col gap-5 md:gap-[clamp(14px,1.16vw,20px)]"
            >
              <div className="flex items-center justify-between mb-2 md:mb-[clamp(5px,0.46vw,8px)]">
                <div className="flex items-center gap-3 md:gap-[clamp(8px,0.69vw,12px)]">
                  <div className="w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-white" />
                  <span className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold tracking-widest uppercase text-white/40">Voqals Dataset</span>
                </div>
                <div className="flex gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[9px] md:text-[clamp(6px,0.52vw,9px)] font-bold uppercase tracking-widest">
                  <span className="text-white/60">Pause</span>
                  <span className="text-white/80">Filler</span>
                </div>
              </div>
              <p className="text-[length:var(--text-body)] leading-relaxed text-white/90 font-medium">
                "Ma'am, I completely understand that you're upset about the delay and we're actively working on resolving the issue. <span className="text-white/80">Um,</span> can I please, <span className="text-white/80">uh,</span> place you on hold for a moment <span className="text-white/60 italic">[pause]</span> while I check the status?"
              </p>
            </motion.div>
          )}
          {activeTab === 'empathetic' && (
            <motion.div
              key="empathetic"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col gap-5 md:gap-[clamp(14px,1.16vw,20px)]"
            >
              <div className="flex items-center justify-between mb-2 md:mb-[clamp(5px,0.46vw,8px)]">
                <div className="flex items-center gap-3 md:gap-[clamp(8px,0.69vw,12px)]">
                  <div className="w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-[#56CFFC]" />
                  <span className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold tracking-widest uppercase text-white/40">Voqals Dataset</span>
                </div>
                <div className="flex gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[9px] md:text-[clamp(6px,0.52vw,9px)] font-bold uppercase tracking-widest">
                  <span className="text-[#FF56A5]">Breath</span>
                  <span className="text-[#50E3C2]">Pause</span>
                  <span className="text-[#56CFFC]">Emphasis</span>
                  <span className="text-yellow-400">Tone Shift</span>
                </div>
              </div>
              <p className="text-[length:var(--text-body)] leading-relaxed text-white/90 font-medium">
                "Ma'am, I <span className="text-[#56CFFC] font-bold">completely</span> understand that you're upset about the delay and <span className="text-[#50E3C2] italic">[short pause]</span> we're <span className="text-[#56CFFC] font-bold">actively</span> working on resolving the issue. <span className="text-yellow-400 italic">[tone shift]</span> <span className="text-[#FF56A5] italic">[short intake]</span> Um, <span className="text-[#56CFFC] font-bold">can I please</span> place you on hold for a moment <span className="text-[#50E3C2] italic">[pause]</span> while I check the status?"
              </p>
            </motion.div>
          )}
          {activeTab === 'urgent' && (
            <motion.div
              key="urgent"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col gap-5 md:gap-[clamp(14px,1.16vw,20px)]"
            >
              <div className="flex items-center justify-between mb-2 md:mb-[clamp(5px,0.46vw,8px)]">
                <div className="flex items-center gap-3 md:gap-[clamp(8px,0.69vw,12px)]">
                  <div className="w-2 h-2 md:w-[clamp(5px,0.46vw,8px)] md:h-[clamp(5px,0.46vw,8px)] rounded-full bg-[#FF56A5]" />
                  <span className="text-[10px] md:text-[clamp(7px,0.58vw,10px)] font-bold tracking-widest uppercase text-white/40">Voqals Dataset</span>
                </div>
                <div className="flex gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[9px] md:text-[clamp(6px,0.52vw,9px)] font-bold uppercase tracking-widest">
                  <span className="text-yellow-400">Fast Pace</span>
                  <span className="text-[#FF56A5]">Rushed</span>
                  <span className="text-[#56CFFC]">Emphasis</span>
                  <span className="text-[#50E3C2]">Louder</span>
                </div>
              </div>
              <p className="text-[length:var(--text-body)] leading-relaxed text-white/90 font-medium">
                "<span className="text-yellow-400 italic">[fast]</span> Ma'am I completely understand that you're upset about the delay and we're <span className="text-[#56CFFC] font-bold">actively</span> working on resolving the issue. <span className="text-[#FF56A5] italic">[rushed]</span> Um, <span className="text-[#50E3C2]">[slightly louder]</span> can I please place you on hold for a moment while I check the status?"
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* Metadata Chips */}
      <div className="pt-4 md:pt-[clamp(11px,0.93vw,16px)] border-t border-white/5 shrink-0">
        {/* Specs row — same for all tabs */}
        <div className="flex items-center gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[11px] md:text-[clamp(7px,0.64vw,11px)] mb-3 md:mb-[clamp(8px,0.69vw,12px)]">
          <span className="font-bold text-white/30 uppercase tracking-widest w-16 md:w-[clamp(43px,3.7vw,64px)] text-right shrink-0">Specs</span>
          <div className="flex flex-wrap gap-x-3 md:gap-x-[clamp(8px,0.69vw,12px)] gap-y-1">
            <span className="text-white/50 font-mono">48 kHz</span>
            <span className="text-white/10">•</span>
            <span className="text-white/50 font-mono">24-bit</span>
            <span className="text-white/10">•</span>
            <span className="text-white/50 font-mono">Mono</span>
            <span className="text-white/10">•</span>
            <span className="text-white/50 font-mono">{(duration / 1000).toFixed(1)}s</span>
          </div>
        </div>
        {activeTab === 'neutral' && (
          <div className="flex flex-col gap-3 md:gap-[clamp(8px,0.69vw,12px)]">
            <div className="flex items-center gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[11px] md:text-[clamp(7px,0.64vw,11px)]">
              <span className="font-bold text-white/30 uppercase tracking-widest w-16 md:w-[clamp(43px,3.7vw,64px)] text-right shrink-0">Prosody</span>
              <div className="flex flex-wrap gap-x-3 md:gap-x-[clamp(8px,0.69vw,12px)] gap-y-1">
                <span className="text-white/80">Even Pitch Contours</span>
                <span className="text-white/10">•</span>
                <span className="text-white/80">Measured Speech Rate</span>
              </div>
            </div>
            <div className="flex items-center gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[11px] md:text-[clamp(7px,0.64vw,11px)]">
              <span className="font-bold text-white/30 uppercase tracking-widest w-16 md:w-[clamp(43px,3.7vw,64px)] text-right shrink-0">Texture</span>
              <div className="flex flex-wrap gap-x-3 md:gap-x-[clamp(8px,0.69vw,12px)] gap-y-1">
                <span className="text-white/80">Natural Pauses</span>
                <span className="text-white/10">•</span>
                <span className="text-white/80">Subtle Fillers</span>
                <span className="text-white/10">•</span>
                <span className="text-white/80">Clear Articulation</span>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'empathetic' && (
          <div className="flex flex-col gap-3 md:gap-[clamp(8px,0.69vw,12px)]">
            <div className="flex items-center gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[11px] md:text-[clamp(7px,0.64vw,11px)]">
              <span className="font-bold text-white/30 uppercase tracking-widest w-16 md:w-[clamp(43px,3.7vw,64px)] text-right shrink-0">Prosody</span>
              <div className="flex flex-wrap gap-x-3 md:gap-x-[clamp(8px,0.69vw,12px)] gap-y-1">
                <span className="text-white/80">Soft Pitch Contours</span>
                <span className="text-white/10">•</span>
                <span className="text-white/80">Slowed Speech Rate</span>
                <span className="text-white/10">•</span>
                <span className="text-white/80">Empathetic Tone Shift</span>
              </div>
            </div>
            <div className="flex items-center gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[11px] md:text-[clamp(7px,0.64vw,11px)]">
              <span className="font-bold text-white/30 uppercase tracking-widest w-16 md:w-[clamp(43px,3.7vw,64px)] text-right shrink-0">Texture</span>
              <div className="flex flex-wrap gap-x-3 md:gap-x-[clamp(8px,0.69vw,12px)] gap-y-1">
                <span className="text-[#FF56A5]/80">Short Intakes</span>
                <span className="text-white/10">•</span>
                <span className="text-[#50E3C2]/80">Deliberate Pauses</span>
                <span className="text-white/10">•</span>
                <span className="text-[#56CFFC]/80">Warm Emphasis</span>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'urgent' && (
          <div className="flex flex-col gap-3 md:gap-[clamp(8px,0.69vw,12px)]">
            <div className="flex items-center gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[11px] md:text-[clamp(7px,0.64vw,11px)]">
              <span className="font-bold text-white/30 uppercase tracking-widest w-16 md:w-[clamp(43px,3.7vw,64px)] text-right shrink-0">Prosody</span>
              <div className="flex flex-wrap gap-x-3 md:gap-x-[clamp(8px,0.69vw,12px)] gap-y-1">
                <span className="text-white/80">Accelerated Rate</span>
                <span className="text-white/10">•</span>
                <span className="text-white/80">Raised Volume</span>
              </div>
            </div>
            <div className="flex items-center gap-4 md:gap-[clamp(11px,0.93vw,16px)] text-[11px] md:text-[clamp(7px,0.64vw,11px)]">
              <span className="font-bold text-white/30 uppercase tracking-widest w-16 md:w-[clamp(43px,3.7vw,64px)] text-right shrink-0">Texture</span>
              <div className="flex flex-wrap gap-x-3 md:gap-x-[clamp(8px,0.69vw,12px)] gap-y-1">
                <span className="text-yellow-400/80">Rushed Delivery</span>
                <span className="text-white/10">•</span>
                <span className="text-[#56CFFC]/80">Stressed Emphasis</span>
                <span className="text-white/10">•</span>
                <span className="text-[#50E3C2]/80">Heightened Energy</span>
              </div>
            </div>
          </div>
        )}
      </div>
      </motion.div>
      </div>
    </div>
  );
};
/* ── USP3: Audio sources ── */
const USP3_AUDIO: Record<string, string> = {
  neutral: '/Audio/Neutral.wav',
  empathetic: '/Audio/Empathetic.wav',
  urgent: '/Audio/Urgent.wav',
};
/* ── USP3: Extract real waveform from audio file ── */
const waveformCache: Record<string, number[]> = {};
function useWaveform(url: string, bars: number = 100, enabled: boolean = true) {
  const [data, setData] = useState<number[]>(() => waveformCache[url] || Array(bars).fill(5));
  useEffect(() => {
    if (!enabled) return;
    if (waveformCache[url]) { setData(waveformCache[url]); return; }
    let cancelled = false;
    fetch(url)
      .then(r => r.arrayBuffer())
      .then(buf => new (window.AudioContext || (window as any).webkitAudioContext)().decodeAudioData(buf))
      .then(decoded => {
        if (cancelled) return;
        const raw = decoded.getChannelData(0);
        const step = Math.floor(raw.length / bars);
        const peaks: number[] = [];
        for (let i = 0; i < bars; i++) {
          let max = 0;
          for (let j = i * step; j < (i + 1) * step && j < raw.length; j++) {
            const abs = Math.abs(raw[j]);
            if (abs > max) max = abs;
          }
          peaks.push(max);
        }
        const maxPeak = Math.max(...peaks, 0.01);
        const normalized = peaks.map(p => Math.max(4, (p / maxPeak) * 100));
        waveformCache[url] = normalized;
        setData(normalized);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [url, bars, enabled]);
  return data;
}
/* ── USP3: Desktop layout (heading + widget side by side) ── */
const Usp3Creativity = ({ onOpenTalentForm }: { onOpenTalentForm: () => void }) => {
  const [activeTab, setActiveTab] = useState<'neutral' | 'empathetic' | 'urgent'>('empathetic');
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [duration, setDuration] = useState(3000);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const usp3Ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = usp3Ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!visible) return;
    const audio = new Audio(USP3_AUDIO[activeTab]);
    audioRef.current = audio;
    audio.addEventListener('loadedmetadata', () => setDuration(audio.duration * 1000));
    audio.addEventListener('ended', () => { setIsPlaying(false); setPlaybackProgress(0); });
    return () => { audio.pause(); audio.src = ''; };
  }, [activeTab, visible]);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    let raf: number;
    const tick = () => {
      if (audio.duration) setPlaybackProgress(audio.currentTime / audio.duration);
      raf = requestAnimationFrame(tick);
    };
    if (isPlaying) { audio.play().catch(() => {}); raf = requestAnimationFrame(tick); }
    else { audio.pause(); }
    return () => cancelAnimationFrame(raf);
  }, [isPlaying]);
  const togglePlay = () => {
    if (playbackProgress >= 1) { const a = audioRef.current; if (a) a.currentTime = 0; setPlaybackProgress(0); }
    setIsPlaying(!isPlaying);
  };
  const handleTabChange = (tab: 'neutral' | 'empathetic' | 'urgent') => {
    setActiveTab(tab);
    setIsPlaying(false);
    setPlaybackProgress(0);
  };
  const waveform = useWaveform(USP3_AUDIO[activeTab], 120, visible);
  return (
    <div ref={usp3Ref} className="max-w-[calc(var(--max-content)-2*var(--px-container))] w-full mx-auto grid grid-cols-1 md:grid-cols-12 gap-[var(--gap-medium)] items-center pointer-events-auto">
      {/* Left Column */}
      <div className="md:col-span-5 flex flex-col justify-center">
        <h3 className="font-display text-[length:var(--heading-usp)] md:text-[clamp(36px,3.13vw,54px)] font-black mb-[clamp(8px,0.93vw,16px)] text-white leading-[1.1] tracking-tighter uppercase overflow-visible">
          DIRECTED FOR<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic pr-[0.15em]">REAL EMOTIONS.</span>
        </h3>
        <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed mb-[clamp(16px,1.39vw,24px)] max-w-lg">
          Flat recordings produce flat AI. Every Voqals session is directed by experienced voice UI directors who understand both the craft of performance and the technical requirements of AI training data. Every utterance is tagged with intent, emotion, persona, and action metadata.
        </p>
      </div>
      {/* Right Column */}
      <div className="md:col-span-7 relative">
        <Usp3ExpandedWidget activeTab={activeTab} handleTabChange={handleTabChange} isPlaying={isPlaying} togglePlay={togglePlay} playbackProgress={playbackProgress} duration={duration} waveform={waveform} />
      </div>
      {/* Swipe-in Voice Talent CTA */}
      <motion.div 
        initial={{ opacity: 0, x: 100, rotate: 5 }}
        whileInView={{ opacity: 1, x: 0, rotate: 0 }}
        transition={{ delay: 0.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        viewport={{ once: false, amount: 0.5 }}
        className="hidden md:block absolute bottom-8 right-8 z-50"
      >
        <button onClick={onOpenTalentForm} className="flex items-center gap-4 p-4 rounded-2xl bg-black/60 border border-[#56CFFC]/30 backdrop-blur-xl group hover:bg-black/80 hover:border-[#56CFFC] transition-all duration-300 shadow-[0_10px_40px_rgba(0,0,0,0.5)] text-left">
          <div className="w-12 h-12 rounded-full bg-[#56CFFC]/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-[0_0_15px_rgba(86,207,252,0.3)]">
            <Sparkles className="w-6 h-6 text-[#56CFFC]" />
          </div>
          <div className="pr-2">
            <p className="text-[10px] uppercase tracking-widest font-bold mb-1">Voice talent?</p>
            <p className="text-sm text-white font-medium flex items-center gap-2 group-hover:text-[#56CFFC] transition-colors">
              Join our roster
              <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
            </p>
          </div>
        </button>
      </motion.div>
    </div>
  );
};
/* ── USP3: Mobile wrapper — scroll-driven peek → fullscreen (like USP1) ── */
const Usp3MobileWrapper = () => {
  const [activeTab, setActiveTab] = useState<'neutral' | 'empathetic' | 'urgent'>('empathetic');
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [duration, setDuration] = useState(3000);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!visible) return;
    const audio = new Audio(USP3_AUDIO[activeTab]);
    audioRef.current = audio;
    audio.addEventListener('loadedmetadata', () => setDuration(audio.duration * 1000));
    audio.addEventListener('ended', () => { setIsPlaying(false); setPlaybackProgress(0); });
    return () => { audio.pause(); audio.src = ''; };
  }, [activeTab, visible]);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    let raf: number;
    const tick = () => {
      if (audio.duration) setPlaybackProgress(audio.currentTime / audio.duration);
      raf = requestAnimationFrame(tick);
    };
    if (isPlaying) { audio.play().catch(() => {}); raf = requestAnimationFrame(tick); }
    else { audio.pause(); }
    return () => cancelAnimationFrame(raf);
  }, [isPlaying]);
  const togglePlay = () => {
    if (playbackProgress >= 1) { const a = audioRef.current; if (a) a.currentTime = 0; setPlaybackProgress(0); }
    setIsPlaying(!isPlaying);
  };
  const handleTabChange = (tab: 'neutral' | 'empathetic' | 'urgent') => {
    setActiveTab(tab);
    setIsPlaying(false);
    setPlaybackProgress(0);
  };
  const waveform = useWaveform(USP3_AUDIO[activeTab], 120, visible);
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollYProgress = useMotionValue(0);
  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    const section = sectionRef.current;
    if (!container || !section) return;
    const handleScroll = () => {
      let sectionTop = 0;
      let el: HTMLElement | null = section;
      while (el && el !== container) {
        sectionTop += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
      }
      const sectionHeight = section.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      const start = sectionTop;
      const end = sectionTop + sectionHeight - containerHeight;
      const progress = Math.max(0, Math.min(1, (scrollTop - start) / (end - start)));
      scrollYProgress.set(progress);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  // Lazy-load audio when section enters viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  // Scroll-driven animation timeline (same as USP1 but shorter max height)
  const headingOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const widgetBorderRadius = useTransform(scrollYProgress, [0.1, 0.6], [24, 16]);
  const widgetMarginLR = useTransform(scrollYProgress, [0, 0.35], [24, 0]);
  const widgetMarginB = useTransform(scrollYProgress, [0.1, 0.6], [16, 0]);
  const widgetHeight = useTransform(scrollYProgress, (v) => {
    const p = Math.min(1, Math.max(0, v / 0.8));
    const vh = window.innerHeight;
    const startH = vh * 0.38;
    const endH = vh - 90;
    return startH + (endH - startH) * p;
  });
  const detailsOpacity = useTransform(scrollYProgress, [0.2, 0.65], [0.7, 1]);
  const detailsBlurAmount = useTransform(scrollYProgress, [0.2, 0.65], [4, 0]);
  const detailsBlurFilter = useMotionTemplate`blur(${detailsBlurAmount}px)`;
  const [isExpanded, setIsExpanded] = useState(false);
  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    const expanded = v > 0.35;
    if (expanded !== isExpanded) setIsExpanded(expanded);
  });
  return (
    <div ref={sectionRef} id="usp3-mobile" className="md:hidden h-[200dvh] w-full relative shrink-0">
      <div className="sticky top-0 h-[100dvh] w-full flex flex-col overflow-hidden">
        {/* Heading */}
        <motion.div
          style={{ opacity: headingOpacity }}
          className="flex-1 flex flex-col items-center justify-center text-center px-[var(--px-container)] overflow-hidden pointer-events-auto pt-[70px]"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)] mb-6">
            Directed Performance
          </div>
          <h3 className="font-display text-[length:var(--heading-usp)] font-black mb-6 text-[#EEEEEE] leading-[1.05] tracking-tighter uppercase overflow-visible">
            DIRECTED FOR<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic pr-[0.15em]">REAL EMOTIONS.</span>
          </h3>
          <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed max-w-sm mx-auto">
            Flat recordings produce flat AI. Every Voqals session is directed by experienced voice UI directors who understand both the craft of performance and the technical requirements of AI training data.
          </p>
        </motion.div>
        {/* Widget */}
        <motion.div
          style={{ borderRadius: widgetBorderRadius, marginLeft: widgetMarginLR, marginRight: widgetMarginLR, marginBottom: widgetMarginB, height: widgetHeight }}
          className="gradient-border shrink-0 pointer-events-auto"
        >
          <div className="w-full h-full overflow-hidden" style={{ borderRadius: 'inherit' }}>
            <Usp3ExpandedWidget
              activeTab={activeTab}
              handleTabChange={handleTabChange}
              isPlaying={isPlaying}
              togglePlay={togglePlay}
              playbackProgress={playbackProgress}
              duration={duration}
              waveform={waveform}
              detailsOpacity={detailsOpacity}
              detailsBlur={detailsBlurFilter}
              isExpanded={isExpanded}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};
const TheDifferenceSection = ({ onOpenTalentForm, onOpenStudioForm }: { onOpenTalentForm: () => void; onOpenStudioForm: () => void }) => {
  const [activeSection, setActiveSection] = useState('overview');
  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    if (!container) return;
    const getVisibleSection = () => {
      const isMobile = window.innerWidth < 768;
      const sectionIds = isMobile
        ? ['overview', 'usp1-mobile', 'usp2-mobile', 'usp3-mobile']
        : ['overview', 'usp1', 'usp2', 'usp3'];
      const scrollTop = container.scrollTop;
      const viewportH = container.clientHeight;
      const viewportCenter = scrollTop + viewportH / 2;
      let closest = 'overview';
      let closestDist = Infinity;
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        let top = 0;
        let node: HTMLElement | null = el;
        while (node && node !== container) {
          top += node.offsetTop;
          node = node.offsetParent as HTMLElement | null;
        }
        const center = top + el.offsetHeight / 2;
        const dist = Math.abs(viewportCenter - center);
        if (dist < closestDist) {
          closestDist = dist;
          closest = id.replace('-mobile', '');
        }
      }
      setActiveSection(closest);
    };
    container.addEventListener('scroll', getVisibleSection, { passive: true });
    getVisibleSection();
    return () => container.removeEventListener('scroll', getVisibleSection);
  }, []);
  const showStickyHeader = activeSection !== 'overview';
  return (
    <div id="difference" className="relative w-full">
      {/* Sticky Header Container */}
      <div className="hidden md:block absolute top-0 left-0 w-full h-full pointer-events-none z-50">
        <div className="sticky top-[clamp(90px,6.3vw,115px)] w-full flex justify-center">
          <AnimatePresence>
            {showStickyHeader && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="pointer-events-auto flex flex-col items-center gap-[clamp(8px,0.93vw,16px)]"
              >
                <motion.div
                  layoutId="difference-header"
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="inline-flex items-center gap-[clamp(4px,0.46vw,8px)] px-[clamp(16px,1.39vw,24px)] py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]"
                >
                  <motion.span layout="position" transition={{ duration: 0.5, ease: "easeOut" }}>The Voqals Advantage</motion.span>
                </motion.div>
                {/* Text-based Progress Indicator */}
                <div className="flex items-center gap-[clamp(8px,0.69vw,12px)] text-[clamp(8px,0.69vw,12px)] font-medium tracking-widest uppercase mt-[clamp(4px,0.46vw,8px)]">
                  <button
                    onClick={() => scrollToId('usp1')}
                    className={`transition-all duration-300 relative ${activeSection === 'usp1' ? 'text-[#56CFFC]' : 'text-white/30 hover:text-white/50'}`}
                  >
                    <span className="relative z-10">Authentic Code-Switching</span>
                  </button>
                  <div className="flex items-center gap-[clamp(4px,0.46vw,8px)] text-white/10">
                    <div className="w-[clamp(10px,0.93vw,16px)] h-[1px] bg-white/10" />
                  </div>
                  <button
                    onClick={() => scrollToId('usp2')}
                    className={`transition-all duration-300 relative ${activeSection === 'usp2' ? 'text-[#56CFFC]' : 'text-white/30 hover:text-white/50'}`}
                  >
                    <span className="relative z-10">The Voqals Quality Standard</span>
                  </button>
                  <div className="flex items-center gap-[clamp(4px,0.46vw,8px)] text-white/10">
                    <div className="w-[clamp(10px,0.93vw,16px)] h-[1px] bg-white/10" />
                  </div>
                  <button
                    onClick={() => {
                      const id = window.innerWidth < 768 ? 'usp3-mobile' : 'usp3';
                      scrollToId(id);
                    }}
                    className={`transition-all duration-300 relative ${activeSection === 'usp3' ? 'text-[#56CFFC]' : 'text-white/30 hover:text-white/50'}`}
                  >
                    <span className="relative z-10">Directed Performance</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      {/* Section Title & Overview */}
      <section id="overview" className="min-h-[100dvh] w-full shrink-0 relative flex flex-col items-center md:justify-center px-[var(--px-container)] pt-24 md:pt-[clamp(60px,5.5vw,96px)] pb-4 overflow-hidden">

        {/* Mobile Dynamic Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[60%] bg-gradient-to-br from-[#1B36FF]/10 to-[#56CFFC]/10 blur-[80px] rounded-full hidden pointer-events-none animate-pulse" style={{ animationDuration: '5s' }} />
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: false, amount: 0.3 }}
          className="text-center w-full z-10 mb-6 md:mb-[clamp(16px,1.85vw,32px)] flex flex-col items-center justify-center flex-1 md:flex-initial"
        >
          <div className="h-10 md:h-[clamp(28px,2.31vw,40px)] mb-6 md:mb-[clamp(16px,1.39vw,24px)] flex items-center justify-center">
            <AnimatePresence>
              {!showStickyHeader && (
                <motion.div
                  layoutId="difference-header"
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="inline-flex items-center gap-2 px-6 md:px-[clamp(16px,1.39vw,24px)] py-2.5 md:py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-xs md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]"
                >
                  <motion.span layout="position" transition={{ duration: 0.5, ease: "easeOut" }}>The Voqals Advantage</motion.span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <h2 className="font-display text-[clamp(36px,11.87vw,52px)] md:text-[length:var(--heading-usp)] font-black tracking-tighter leading-[1.05] text-[#EEEEEE]">
            MESSY REALITY.<br className="hidden md:block" />{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">FLAWLESS DATA.</span>
          </h2>
          <p className="mt-6 md:mt-[clamp(16px,1.39vw,24px)] max-w-sm md:max-w-3xl mx-auto text-white/90 text-[length:var(--text-body)] leading-relaxed px-0">
            To build AI that truly understands India, sheer data volume isn't enough. You need context, precision, and emotion. We close the gap between how people speak and how models learn by combining the messy reality of natural code-switching with uncompromising audio quality and expertly performed and directed vocal performance.
          </p>
        </motion.div>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: false, amount: 0.3 }}
          className="hidden md:grid md:grid-cols-3 gap-[clamp(12px,1.4vw,24px)] max-w-[var(--max-content)] w-full mx-auto z-10 overflow-x-auto snap-x snap-mandatory py-[clamp(12px,1.16vw,20px)] no-scrollbar px-[var(--px-container)]"
        >
          {[
            {
              title: "Authentic Code-Switching",
              subtitle: "Real-World Speech",
              desc: "We capture natural, complex code-switched speech so your model understands how India actually communicates, not a sanitized, single-language approximation.",
              color: "from-[#1B36FF]/20",
              id: "usp1"
            },
            {
              title: "Voqals Quality Standard",
              subtitle: "Engineered Purity",
              desc: "Our proprietary studio certification guarantees \u221260dB noise floor, under 200ms RT60, and zero electrical interference. Absolute audio purity.",
              color: "from-[#56CFFC]/20",
              id: "usp2"
            },
            {
              title: "Directed Performance",
              subtitle: "Directed Expression",
              desc: "Every session is directed by experienced Voice UI directors who extract real emotion, genuine intent, and naturalistic delivery so your model sounds like a real person.",
              color: "from-[#1B36FF]/20",
              id: "usp3"
            },
          ].map((box, i) => (
            <div key={i} className="h-auto min-h-0 w-auto shrink snap-center glass-panel p-[clamp(20px,1.85vw,32px)] rounded-[clamp(20px,1.85vw,32px)] border border-white/10 flex flex-col items-start text-left group hover:border-[#56CFFC]/50 transition-all duration-500 relative overflow-hidden hover:-translate-y-2 cursor-default" onClick={() => { if(window.innerWidth < 768) scrollToId(box.id) }}>
              <div className={`absolute inset-0 bg-gradient-to-br ${box.color} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              <div className="flex-1 flex flex-col justify-start w-full mt-1">
                <h3 className="font-display text-[length:var(--heading-card)] font-black mb-[clamp(4px,0.46vw,8px)] text-white relative z-10 tracking-tight leading-tight">{box.title}</h3>
                <div className="text-[clamp(8px,0.69vw,12px)] font-bold uppercase tracking-widest md:mb-[clamp(8px,0.93vw,16px)] relative z-10">{box.subtitle}</div>
                <p className="hidden md:block text-white/90 text-[clamp(10px,0.81vw,14px)] leading-relaxed mb-[clamp(16px,1.85vw,32px)] relative z-10">
                  {box.desc}
                </p>
              </div>
              <div className="hidden md:block mt-auto relative z-10 w-full">
                <div className="h-px w-full bg-white/10 mb-[clamp(8px,0.93vw,16px)] group-hover:bg-white/20 transition-colors" />
                <button
                  onClick={(e) => { e.stopPropagation(); scrollToId(box.id); }}
                  className="flex items-center justify-between w-full text-[clamp(8px,0.69vw,12px)] font-bold text-white uppercase tracking-[0.2em] group/btn"
                >
                  <span>See how we do it</span>
                  <div className="w-[clamp(22px,1.85vw,32px)] h-[clamp(22px,1.85vw,32px)] rounded-full bg-white/5 flex items-center justify-center group-hover/btn:bg-[#56CFFC] group-hover/btn:text-black transition-colors">
                    <ArrowDown className="w-[clamp(11px,0.93vw,16px)] h-[clamp(11px,0.93vw,16px)] transform group-hover/btn:translate-y-1 transition-transform duration-300" />
                  </div>
                </button>
              </div>
              <div className="hidden absolute bottom-4 right-4 text-[#56CFFC]/50 group-hover:text-[#56CFFC] transition-colors">
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          ))}
        </motion.div>
        {/* Mobile spacer to push card to bottom */}
        {/* Mobile-only consolidated USP card — styled like Why India widget */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: false, amount: 0.1 }}
          className="md:hidden w-full z-10"
        >
          <div className="gradient-border rounded-2xl backdrop-blur-xl shadow-2xl relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}>
            <div className="px-5 py-4 flex flex-col overflow-hidden rounded-[inherit]">
              {/* Header row — mimics play bar label style */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full bg-[#56CFFC] opacity-80" />
                  <span className="text-[10px] font-mono text-white/40 uppercase tracking-[0.1em]">The Voqals Advantage · 3 Pillars</span>
                </div>
              </div>
              {/* Divider */}
              <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent mb-3" />
              {/* USP list items */}
              <div className="flex flex-col">
              {[
                { title: "Authentic Code-Switching", subtitle: "Real-World Speech", id: "usp1-mobile" },
                { title: "The Voqals Quality Standard", subtitle: "Engineered Purity", id: "usp2-mobile" },
                { title: "Directed Performance", subtitle: "Directed Expression", id: "usp3-mobile" },
              ].map((item, i) => (
                <div key={i} onClick={() => scrollToId(item.id)} className={"flex items-center gap-2 py-2.5 cursor-pointer group/row"}>
                  <div className="flex-1 min-w-0 truncate">
                    <span className="font-display font-normal text-[13px] tracking-tight text-white/80">{item.title}</span>
                  </div>
                  <span className="text-[8px] font-medium uppercase tracking-widest text-white/20 shrink-0">{item.subtitle}</span>
                  <ArrowRight className="w-3 h-3 text-white/10 group-hover/row:text-[#56CFFC] group-hover/row:translate-x-0.5 transition-all shrink-0" />
                </div>
              ))}
              </div>
              {/* Divider */}
              <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent mt-1 mb-3" />
              {/* CTA row */}
              <button
                onClick={() => scrollToId('usp1-mobile')}
                className="w-full flex items-center justify-center gap-2.5 text-[10px] font-bold text-white/40 uppercase tracking-[0.2em] group/btn hover:text-[#56CFFC] transition-colors"
              >
                <span>Explore</span>
                <ArrowDown className="w-3 h-3 transform group-hover/btn:translate-y-0.5 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </motion.div>
      </section>
      {/* USP 1: Mobile scroll-driven peek→fullscreen / Desktop side-by-side */}
      <Usp1MobileWrapper />
      <section id="usp1" className="hidden md:flex min-h-[100dvh] w-full shrink-0 relative items-center justify-center px-[var(--px-container)] pt-[clamp(96px,10.2vw,176px)] pb-[clamp(4px,0.46vw,8px)]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false, amount: 0.1 }}
          className="w-full flex justify-center"
        >
          <Usp1Complexity />
        </motion.div>
      </section>
      {/* USP 2: Mobile */}
      <Usp2MobileWrapper />
      {/* USP 2: Technicality (Desktop) */}
      <section id="usp2" className="hidden md:flex min-h-screen w-full shrink-0 relative items-center justify-center px-[var(--px-container)] pt-[clamp(96px,10.2vw,176px)] pb-[clamp(4px,0.46vw,8px)] overflow-hidden">
        <div className="w-full flex justify-center">
          <Usp2Technicality />
        </div>
        {/* Swipe-in Studio Certification CTA */}
        <motion.div 
          initial={{ opacity: 0, x: 100, rotate: 5 }}
          whileInView={{ opacity: 1, x: 0, rotate: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: false, amount: 0.5 }}
          className="hidden md:block absolute bottom-8 right-8 z-50"
        >
          <button onClick={onOpenStudioForm} className="flex items-center gap-4 p-4 rounded-2xl bg-black/60 border border-[#56CFFC]/30 backdrop-blur-xl group hover:bg-black/80 hover:border-[#56CFFC] transition-all duration-300 shadow-[0_10px_40px_rgba(0,0,0,0.5)] text-left">
            <div className="w-12 h-12 rounded-full bg-[#56CFFC]/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-[0_0_15px_rgba(86,207,252,0.3)]">
              <Mic2 className="w-6 h-6 text-[#56CFFC]" />
            </div>
            <div className="pr-2">
              <p className="text-[10px] uppercase tracking-widest font-bold mb-1">Are you a studio?</p>
              <p className="text-sm text-white font-medium flex items-center gap-2 group-hover:text-[#56CFFC] transition-colors">
                Get Voqals Certified
                <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
              </p>
            </div>
          </button>
        </motion.div>
      </section>
      {/* USP 3: Mobile scroll-driven widget */}
      <Usp3MobileWrapper />
      {/* USP 3: Creativity (Desktop) */}
      <section id="usp3" className="hidden md:flex min-h-screen w-full shrink-0 relative items-center justify-center px-[var(--px-container)] pt-[clamp(96px,10.2vw,176px)] pb-[clamp(4px,0.46vw,8px)] overflow-hidden">
        <div className="w-full flex justify-center">
          <Usp3Creativity onOpenTalentForm={onOpenTalentForm} />
        </div>
      </section>
    </div>
  );
};
const CustomDataCollection = ({ onStartProject }: { onStartProject: () => void }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
  };
  return (
    <div className="max-w-[var(--max-content)] w-full mx-auto flex-1 flex flex-col items-center text-center z-10 relative">
      {/* Background Glow */}
      <motion.div
        animate={{ scale: [1, 1.05, 1], opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[500px] bg-[#1B36FF]/15 blur-[150px] rounded-full pointer-events-none"
      />
      {/* Content wrapper - vertically centered */}
      <div className="flex-1 flex flex-col items-center justify-center w-full">
      {/* Top Label */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: false, amount: 0.3 }}
        className="mb-6 md:mb-[clamp(24px,2.31vw,40px)]"
      >
        <div className="inline-flex items-center gap-2 px-5 py-2 md:gap-[clamp(4px,0.46vw,8px)] md:px-[clamp(16px,1.39vw,24px)] md:py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
          Custom Data Collection
        </div>
      </motion.div>
      {/* Headlines */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: false, amount: 0.3 }}
        className="mb-8 md:mb-[clamp(22px,1.85vw,32px)] relative flex flex-col items-center"
      >
        <h2 className="font-display text-[clamp(28px,9.2vw,56px)] md:text-[length:var(--heading-hero)] font-black tracking-tighter leading-[1.05] uppercase">
          INDIAN SPEECH DATA.<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">AS A SERVICE.</span>
        </h2>
      </motion.div>
      {/* Body Copy */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: false, amount: 0.3 }}
        className="text-white/90 text-[length:var(--text-body)] max-w-sm md:max-w-[clamp(540px,46.3vw,960px)] mx-auto leading-relaxed mb-8 md:mb-[clamp(32px,2.78vw,48px)]"
      >
        Need data that doesn't exist yet? We build it. You define the use case, the languages, the personas, the emotional range, and the volume. We design the collection strategy, source the right talent, run certified recording sessions, handle post-production and annotation, and deliver structured, SFT-ready files to your exact schema.
      </motion.p>
      {/* CTA Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: false, amount: 0.3 }}
        className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-[clamp(14px,1.16vw,20px)] w-full md:w-auto mb-12 md:mb-[clamp(32px,2.78vw,48px)]"
      >
        <Link
          to="/custom-data-collection/"
          className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,40px)] py-[clamp(14px,1.16vw,20px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,12px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
          <span className="relative z-10 flex items-center gap-2">
            Explore Custom Data Collection
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </Link>
        <button onClick={onStartProject} className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,40px)] py-[clamp(14px,1.16vw,20px)] rounded-full border border-white/20 text-white text-[clamp(10px,0.69vw,12px)] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all hover:bg-white/5 hover:text-white hover:border-white/40 flex items-center justify-center whitespace-nowrap">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
          <span className="relative z-10 flex items-center gap-2">
            Start a project
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </button>
      </motion.div>
      {/* Quick connect — softer entry path next to the primary CTAs */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        viewport={{ once: false, amount: 0.3 }}
        className="flex justify-center w-full mb-12 md:mb-[clamp(32px,2.78vw,48px)]"
      >
        <QuickConnectForm source="Quick Connect — Home / Custom Data CTA" />
      </motion.div>
      </div>
      {/* Pipeline Services — Mobile: marquee, Desktop: static */}
      <div className="md:hidden relative z-20 w-full pt-3 pb-6 overflow-hidden">
        <div className="flex w-max animate-marquee">
          {[1, 2].map((set) => (
            <div key={set} className="flex items-center">
              {["Scenario Engineering", "Precision Casting", "Certified Recording", "Post-Production & QA", "Structured Delivery"].map((service, index) => (
                <div key={`${set}-${index}`} className="flex items-center group px-8">
                  <span className="text-[10px] font-display font-medium text-white/90 uppercase tracking-[0.3em] group-hover:text-white transition-colors duration-500 cursor-default">
                    {service}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="hidden md:flex relative z-20 w-full max-w-[var(--max-content)] mx-auto justify-between items-center pt-[clamp(11px,0.93vw,16px)] pb-[clamp(22px,1.85vw,32px)] px-[var(--px-container)]">
        {["Scenario Engineering", "Precision Casting", "Certified Recording", "Post-Production & QA", "Structured Delivery"].map((service, index) => (
          <React.Fragment key={index}>
            {index > 0 && <div className="w-1 h-1 rounded-full bg-white/20 shrink-0" />}
            <span className="text-[clamp(8px,0.69vw,12px)] font-display font-medium text-white/40 uppercase tracking-[0.4em] hover:text-white/70 transition-colors duration-500 cursor-default whitespace-nowrap">
              {service}
            </span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
const DesktopPipelineAccordion = ({ steps, onStartProject }: { steps: { title: string; subtitle: string; pointers: string[]; tags: string[]; icon: React.ReactNode }[]; onStartProject: () => void }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  return (
    <div className="hidden md:flex items-stretch max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] gap-[var(--gap-large)]">
      {/* Left Side — Pill + Heading + CTA */}
      <div className="w-[40%] flex flex-col shrink-0 text-left">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: false, amount: 0.3 }}
          className="flex flex-col items-start h-full justify-center"
        >
          {/* Pill */}
          <div className="mb-[clamp(16px,1.39vw,24px)] inline-flex items-center gap-[clamp(4px,0.46vw,8px)] px-[clamp(16px,1.39vw,24px)] py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)] w-fit">
            Custom Data Collection
          </div>
          {/* Heading + Body + CTA */}
          <div className="flex flex-col items-start">
            <h2 className="font-display text-[length:var(--heading-pipeline)] font-black text-white leading-[1.1] tracking-tighter uppercase mb-[clamp(16px,1.39vw,24px)]">
              DATA COLLECTION <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">PIPELINE.</span>
            </h2>
            <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed max-w-[clamp(303px,25.93vw,448px)] mb-[clamp(27px,2.31vw,40px)]">
              Tell us what your model needs to master. We engineer the execution. Our fully managed pipeline handles the entire complexity of custom data creation—from the blank page to the flawlessly structured JSON.
            </p>
            <button onClick={onStartProject} className="group relative px-[clamp(22px,1.85vw,32px)] py-[clamp(11px,0.93vw,16px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(8px,0.69vw,12px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-[clamp(8px,0.69vw,12px)] overflow-hidden shadow-[0_0_20px_rgba(27,54,255,0.3)] hover:shadow-[0_0_30px_rgba(86,207,252,0.4)] hover:scale-[1.02] w-fit">
              <span className="relative z-10 flex items-center gap-[clamp(5px,0.46vw,8px)]">
                Start Your Project
                <ArrowRight className="w-[clamp(11px,0.93vw,16px)] h-[clamp(11px,0.93vw,16px)] group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
      {/* Right Side — Dynamic Accordion */}
      <div className="w-[60%] flex flex-col justify-center gap-[clamp(4px,0.35vw,6px)] relative">
        {steps.map((step, index) => {
          const isActive = index === activeIndex;
          return (
            <div
              key={step.title}
              onClick={() => setActiveIndex(index)}
              className="cursor-pointer relative rounded-[clamp(11px,0.93vw,16px)] overflow-hidden"
              style={{
                transform: isActive ? 'scale(1)' : 'scale(0.97)',
                opacity: isActive ? 1 : 0.7,
                background: isActive ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.03)',
                border: isActive ? '1px solid rgba(86,207,252,0.2)' : '1px solid rgba(255,255,255,0.08)',
                boxShadow: isActive ? '0 0 50px rgba(86,207,252,0.1), 0 0 100px rgba(27,54,255,0.05), inset 0 1px 0 rgba(255,255,255,0.08)' : 'none',
                backdropFilter: 'blur(16px)',
                transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              {/* Active glow accent — left bar */}
              <div
                className="absolute left-0 top-[10%] bottom-[10%] w-[2px] origin-center bg-gradient-to-b from-[#56CFFC] via-[#56CFFC] to-[#1B36FF] rounded-full"
                style={{
                  transform: isActive ? 'scaleY(1)' : 'scaleY(0)',
                  opacity: isActive ? 1 : 0,
                  boxShadow: isActive ? '0 0 12px rgba(86,207,252,0.5)' : 'none',
                  transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              />
              {/* Active background shimmer */}
              {isActive && (
                <div className="absolute inset-0 bg-gradient-to-r from-[#56CFFC]/[0.03] via-transparent to-[#1B36FF]/[0.03] pointer-events-none" />
              )}
              {/* Card Header */}
              <div className={`flex items-center gap-[clamp(11px,0.93vw,16px)] px-[clamp(19px,1.62vw,28px)] ${isActive ? 'py-[clamp(11px,0.93vw,16px)]' : 'py-[clamp(7px,0.58vw,10px)]'} transition-all duration-300`}>
                {/* Icon */}
                <div
                  className="w-[clamp(27px,2.31vw,40px)] h-[clamp(27px,2.31vw,40px)] rounded-[clamp(8px,0.69vw,12px)] flex items-center justify-center shrink-0"
                  style={{
                    transform: isActive ? 'scale(1) rotate(0deg)' : 'scale(0.9) rotate(-5deg)',
                    background: isActive ? 'linear-gradient(135deg, rgba(27,54,255,0.3), rgba(86,207,252,0.1))' : 'rgba(255,255,255,0.04)',
                    border: isActive ? '1px solid rgba(86,207,252,0.4)' : '1px solid rgba(255,255,255,0.06)',
                    boxShadow: isActive ? '0 0 20px rgba(86,207,252,0.2)' : 'none',
                    transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                >
                  <div className="scale-[0.8]">{step.icon}</div>
                </div>
                {/* Title */}
                <h3
                  className="flex-1 min-w-0 font-black uppercase tracking-wide"
                  style={{
                    color: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
                    fontSize: isActive ? 'clamp(10px,0.87vw,15px)' : 'clamp(9px,0.81vw,14px)',
                    transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                >
                  {step.title}
                </h3>
                {/* Expand indicator */}
                <div
                  className="w-[clamp(19px,1.62vw,28px)] h-[clamp(19px,1.62vw,28px)] rounded-full flex items-center justify-center shrink-0"
                  style={{
                    transform: isActive ? 'rotate(180deg) scale(1.1)' : 'rotate(0deg) scale(0.8)',
                    background: isActive ? 'rgba(86,207,252,0.15)' : 'rgba(255,255,255,0.03)',
                    border: isActive ? '1px solid rgba(86,207,252,0.3)' : '1px solid rgba(255,255,255,0.05)',
                    transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                >
                  <ChevronDown className="w-[clamp(9px,0.81vw,14px)] h-[clamp(9px,0.81vw,14px)]" style={{ color: isActive ? '#56CFFC' : 'rgba(255,255,255,0.2)', transition: 'color 0.5s' }} />
                </div>
              </div>
              {/* Expandable Body */}
              {isActive && (
                <div className="px-[clamp(19px,1.62vw,28px)] pb-[clamp(14px,1.16vw,20px)] pt-0 ml-[clamp(38px,3.24vw,56px)] max-h-[30vh] overflow-y-auto no-scrollbar">
                  {/* Divider line */}
                  <div className="w-[clamp(32px,2.78vw,48px)] h-px bg-gradient-to-r from-[#56CFFC]/40 to-transparent mb-[clamp(8px,0.69vw,12px)]" />
                  {/* Subtitle */}
                  <p className="text-[#56CFFC]/90 text-[clamp(8px,0.69vw,12px)] font-bold mb-[clamp(11px,0.93vw,16px)] tracking-wider leading-relaxed">{step.subtitle}</p>
                  {/* Pointer list */}
                  <ul className="space-y-[clamp(5px,0.46vw,8px)] mb-[clamp(11px,0.93vw,16px)]">
                    {step.pointers.map((pointer, i) => (
                      <li key={i} className="flex items-start gap-[clamp(8px,0.69vw,12px)] text-[clamp(9px,0.75vw,13px)] leading-relaxed font-medium text-[#A4BDE5]/80">
                        <div className="w-[clamp(4px,0.35vw,6px)] h-[clamp(4px,0.35vw,6px)] rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] mt-[clamp(4px,0.35vw,6px)] shrink-0 shadow-[0_0_6px_rgba(86,207,252,0.4)]" />
                        {pointer}
                      </li>
                    ))}
                  </ul>
                  {/* Tags */}
                  <div className="flex flex-wrap gap-[clamp(4px,0.35vw,6px)]">
                    {step.tags.map((tag, i) => (
                      <span key={i} className="px-[clamp(8px,0.69vw,12px)] py-[clamp(3px,0.23vw,4px)] rounded-full bg-[#56CFFC]/[0.08] border border-[#56CFFC]/20 text-[clamp(7px,0.58vw,10px)] uppercase tracking-[0.15em] font-bold text-[#56CFFC]/80 shadow-[0_0_10px_rgba(86,207,252,0.05)]">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
const DataCollectionPipeline = ({ onStartProject, isProjectOpen, onProjectClose }: { onStartProject: () => void; isProjectOpen?: boolean; onProjectClose?: () => void }) => {
  const steps = [
    {
      title: "SCENARIO ENGINEERING",
      subtitle: "Designing scripts and conversational scenarios for real-world use cases.",
      pointers: [
        "Design scripts, prompts, and conversational scenarios",
        "Build around real-world use cases, not synthetic approximations",
        "Engineer speech patterns your model needs",
        "Map phonetic and emotional boundaries"
      ],
      tags: ["Linguistics", "Context Design", "Prompting"],
      icon: <FileText className="w-6 h-6 text-[#56CFFC]" />
    },
    {
      title: "PRECISION CASTING",
      subtitle: "Talent cast to match your exact persona requirements.",
      pointers: [
        "Cast voice talent that matches your defined persona profiles",
        "Age, region, dialect, register, and speaking style — all specified and verified",
        "Multilingual and code-switching speakers sourced on demand",
        "No mic goes live without demographic and profile verification"
      ],
      tags: ["Persona Matching", "Talent Sourcing", "Dialects"],
      icon: <Mic2 className="w-6 h-6 text-[#56CFFC]" />
    },
    {
      title: "CERTIFIED RECORDING",
      subtitle: "Directed sessions in Voqals Quality Standard-certified studios.",
      pointers: [
        "Voqals Quality Standard-certified studios only",
        "Every session directed by specialised Voice UI directors",
        "Directed for expressiveness, intent, and naturalistic delivery",
        "Real-time monitoring ensures every take meets spec before moving on"
      ],
      tags: ["Acoustics", "Studio Grade", "Voice UI Direction"],
      icon: <Speaker className="w-6 h-6 text-[#56CFFC]" />
    },
    {
      title: "POST-PRODUCTION & QA",
      subtitle: "Cleaned, mastered, and validated to perfection.",
      pointers: [
        "Artifacts like mouth clicks, pops, and breaths cleaned up",
        "Audio mastered for uniform loudness — every token sounds the same",
        "Linguistic QA: accuracy, naturalness, and intent alignment verified",
        "Anything that doesn't pass gets re-recorded, not patched"
      ],
      tags: ["Mastering", "Quality Assurance", "Artifact Removal"],
      icon: <Sliders className="w-6 h-6 text-[#56CFFC]" />
    },
    {
      title: "STRUCTURED DELIVERY",
      subtitle: "Files delivered in your required format, annotated to your schema.",
      pointers: [
        "Delivered in your required format with full metadata",
        "Speaker ID, language, intent, emotion, persona, and action tags",
        "Annotated to your exact schema specifications",
        "Ready to load into your training pipeline immediately"
      ],
      tags: ["Metadata", "SFT-Ready", "Integration"],
      icon: <Database className="w-6 h-6 text-[#56CFFC]" />
    }
  ];
  return (
    <>
      {/* Mobile Layout */}
      <div className="md:hidden w-full flex-1 flex flex-col items-center text-center justify-center overflow-hidden relative">
        {/* Top Section - Pill, Heading, Body */}
        <div className="px-5">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              Custom Data Collection
            </div>
          </div>
          <h2 className="font-display text-[clamp(32px,10.67vw,48px)] font-black text-white leading-[1.1] tracking-tighter uppercase mb-6">
            DATA COLLECTION<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">PIPELINE.</span>
          </h2>
          <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed max-w-sm mx-auto mb-7">
            Tell us what your model needs to master. We engineer the execution. Our fully managed pipeline handles the entire complexity of custom data creation—from the blank page to the flawlessly structured JSON.
          </p>
        </div>
        {/* Horizontal Carousel */}
        <div
          className="w-full relative"
          style={{
            maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
            WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
          }}
        >
          <div className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar gap-3 px-5" style={{ scrollPaddingInline: "20px" }}>
            {steps.map((step, index) => (
              <div
                key={index}
                className="w-[85%] min-w-[85%] shrink-0 snap-center rounded-2xl bg-black/40 border border-white/10 backdrop-blur-md p-4 text-left"
              >
                <h3 className="text-white text-[13px] font-black uppercase tracking-tight mb-0.5">
                  {step.title}
                </h3>
                <h4 className="text-[#56CFFC] text-[11px] font-bold mb-3 tracking-wider">
                  {step.subtitle}
                </h4>
                <ul className="space-y-1.5 mb-3">
                  {step.pointers.map((pointer, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11px] text-white/90 leading-snug font-medium">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#56CFFC] mt-1 shrink-0 opacity-60" />
                      {pointer}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-1.5">
                  {step.tags.map((tag, i) => (
                    <span key={i} className="px-2.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-[8px] uppercase tracking-widest font-bold text-white/60">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* CTA */}
        <div className="px-5 w-full">
          <button onClick={onStartProject} className="group relative w-full mt-5 mb-3 py-3.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[11px] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden shadow-[0_0_20px_rgba(27,54,255,0.3)]">
            <span className="relative z-10 flex items-center gap-2">
              Start Your Project
              <ArrowRight className="w-4 h-4" />
            </span>
          </button>
          {/* Quick connect — softer entry path on mobile */}
          <div className="mt-2 mb-4 flex justify-center">
            <QuickConnectForm source="Quick Connect — Home / Pipeline Mobile" />
          </div>
        </div>
      </div>
      {/* Desktop Layout — Accordion */}
      <DesktopPipelineAccordion steps={steps} onStartProject={onStartProject} />
    </>
  );
};
const datasets = [
  {
    id: 'ds-01',
    name: 'Conversational Assistant',
    category: 'Personas',
    languages: ['Hindi', 'English', 'Hinglish'],
    speakers: 8,
    status: 'In Pre-Production' as const,
    description: 'Natural multi-turn dialogue for building voice assistants that handle real conversational flows.',
  },
  {
    id: 'ds-02',
    name: 'Customer Support',
    category: 'Personas',
    languages: ['Tamil', 'Telugu', 'English'],
    speakers: 6,
    status: 'In Pre-Production' as const,
    description: 'Complaint resolution, escalation, de-escalation, and empathetic agent responses.',
  },
  {
    id: 'ds-03',
    name: 'Film Characters',
    category: 'Personas',
    languages: ['Hindi', 'Marathi'],
    speakers: 6,
    status: 'In Pre-Production' as const,
    description: 'Character-driven, emotionally heightened speech for TTS and voice synthesis.',
  },
  {
    id: 'ds-04',
    name: 'General Population',
    category: 'Personas',
    languages: ['Hindi', 'Bengali', 'Gujarati'],
    speakers: 20,
    status: 'In Pre-Production' as const,
    description: 'Broad-coverage foundational corpus across age groups, regions, and registers.',
  },
  {
    id: 'ds-05',
    name: 'Overlap & Interruption',
    category: 'Training Use Case',
    languages: ['Hinglish', 'Hindi'],
    speakers: 8,
    status: 'In Pre-Production' as const,
    description: 'Real-world conversational dynamics: turn-stealing, overlapping speech, false starts.',
  },
  {
    id: 'ds-06',
    name: 'Emotional Spectrum',
    category: 'Training Use Case',
    languages: ['Hindi', 'English'],
    speakers: 10,
    status: 'In Pre-Production' as const,
    description: 'Full emotional range: joy, frustration, anxiety, sarcasm, urgency, calm.',
  },
  {
    id: 'ds-07',
    name: 'Foundational Monologues',
    category: 'Training Use Case',
    languages: ['Hindi', 'Tamil', 'Telugu', 'Malayalam'],
    speakers: 12,
    status: 'In Pre-Production' as const,
    description: 'Long-form solo speech for foundational pre-training. Diverse speaker profiles.',
  },
  {
    id: 'ds-08',
    name: 'Code-Switching Mastery',
    category: 'Training Use Case',
    languages: ['Hinglish', 'Tanglish'],
    speakers: 8,
    status: 'In Pre-Production' as const,
    description: 'The definitive dataset for training models on natural code-switching.',
  },
];
const ReadyToUseDatasets = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [datasetEnquiryOpen, setDatasetEnquiryOpen] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);
  const deliverablesRef = useRef<HTMLDivElement>(null);
  const mobileSectionRef = useRef<HTMLDivElement>(null);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyEmailError, setNotifyEmailError] = useState('');
  const [notifySubmitting, setNotifySubmitting] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);
  const FREE_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com'];
  const validateNotifyEmail = (val: string) => {
    setNotifyEmail(val);
    if (!val) { setNotifyEmailError(''); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) { setNotifyEmailError('Please enter a valid email.'); return; }
    const domain = val.split('@')[1]?.toLowerCase();
    if (domain && FREE_DOMAINS.includes(domain)) { setNotifyEmailError('Please use your work email.'); return; }
    setNotifyEmailError('');
  };
  const handleNotify = async () => {
    if (!notifyEmail || notifyEmailError || notifySubmitting) return;
    setNotifySubmitting(true);
    try {
      await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae',
          subject: 'Dataset Notify — ' + notifyEmail,
          from_name: 'Voqals Dataset Notify',
          replyto: notifyEmail,
          Email: notifyEmail,
          'Form Type': 'Dataset Launch Notification',
        }),
      });
      setNotifyDone(true);
    } catch { setNotifyDone(true); }
    finally { setNotifySubmitting(false); }
  };
  const mobileScrollProgress = useMotionValue(0);
  const mobileTabRef = useRef<HTMLDivElement>(null);
  const [mobileTabIndicator, setMobileTabIndicator] = useState({ left: 0, width: 0 });
  const filteredDatasets = activeFilter === 'All'
    ? datasets
    : datasets.filter(d => d.category === activeFilter);
  // Mobile scroll-driven widget expansion (manual, matching USP pattern)
  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    const section = mobileSectionRef.current;
    if (!container || !section) return;
    const handleScroll = () => {
      let sectionTop = 0;
      let el: HTMLElement | null = section;
      while (el && el !== container) {
        sectionTop += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
      }
      const sectionHeight = section.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      const start = sectionTop;
      const end = sectionTop + sectionHeight - containerHeight;
      const progress = Math.max(0, Math.min(1, (scrollTop - start) / (end - start)));
      mobileScrollProgress.set(progress);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  // ── Scroll-driven animation timeline ──
  // Heading fades slowly as widget covers it
  // Widget grows from 25% peek to fullscreen
  const mobileHeadingOpacity = useTransform(mobileScrollProgress, [0.15, 0.7], [1, 0]);
  // Heading area shrinks from 70% to 0 as widget grows
  const mobileHeadingHeight = useTransform(mobileScrollProgress, (v) => {
    const p = Math.min(1, Math.max(0, v / 0.8));
    const vh = window.innerHeight;
    const startH = vh * 0.72;
    const endH = 0;
    return startH + (endH - startH) * p;
  });
  // Widget peek is tall enough that CTAs fall below viewport edge
  // heading min-h keeps hero content from collapsing too small initially
  const mobileWidgetHeight = useTransform(mobileScrollProgress, (v) => {
    const p = Math.min(1, Math.max(0, v / 0.8));
    const vh = window.innerHeight;
    const startH = vh * 0.28;
    const endH = vh - 120;
    return startH + (endH - startH) * p;
  });
  const mobileWidgetRadius = useTransform(mobileScrollProgress, [0.1, 0.6], [24, 16]);
  const mobileWidgetMarginLR = useTransform(mobileScrollProgress, [0, 0.35], [24, 0]);
  const mobileWidgetMarginB = useTransform(mobileScrollProgress, [0.1, 0.6], [16, 0]);
  const statusColor = (s: string) => s === 'Ready' ? 'text-emerald-400' : s === 'Production' ? 'text-amber-400' : 'text-amber-400/70';
  const statusDot = (s: string) => s === 'Ready' ? 'bg-emerald-400' : s === 'Production' ? 'bg-amber-400' : 'bg-amber-400/70';
  // Sliding tab indicator (matching USP3 pattern)
  const filterTabs = ['All', 'Personas', 'Training'];
  useEffect(() => {
    const container = mobileTabRef.current;
    if (!container) return;
    const buttons = container.querySelectorAll('button');
    const tabIndex = filterTabs.findIndex(f => (f === 'Training' ? 'Training Use Case' : f) === activeFilter);
    const btn = buttons[tabIndex >= 0 ? tabIndex : 0];
    if (btn) {
      setMobileTabIndicator({ left: (btn as HTMLElement).offsetLeft, width: (btn as HTMLElement).offsetWidth });
    }
  }, [activeFilter]);
  return (
    <>
      {/* Mobile Layout — 200dvh outer for scroll distance, sticky viewport inside */}
      <div ref={mobileSectionRef} id="datasets" className="md:hidden h-[200dvh] w-full relative shrink-0">
        <div className="sticky top-0 h-[100dvh] flex flex-col overflow-hidden pt-20">
          {/* Background Glow */}
          <motion.div
            animate={{ scale: [1, 1.05, 1], opacity: [0.4, 0.6, 0.4] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#56CFFC]/10 blur-[150px] rounded-full pointer-events-none z-0"
          />
          {/* Hero Content — fades out and shrinks on scroll */}
          <motion.div style={{ opacity: mobileHeadingOpacity, height: mobileHeadingHeight }} className="shrink-0 flex flex-col items-center justify-center text-center px-5 z-10 overflow-hidden">
            {/* Pill */}
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
                Ready To Use Datasets
              </div>
            </div>
            {/* Heading */}
            <h2 className="font-display text-[clamp(24px,8vw,48px)] md:text-[length:var(--heading-hero)] font-black text-white leading-[1.05] tracking-tighter uppercase mb-1">
              PRODUCTION DATASETS.
            </h2>
            <h3 className="font-display text-[clamp(24px,8vw,48px)] md:text-[length:var(--heading-hero)] font-black text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic leading-[1.05] tracking-tighter uppercase mb-6">
              LAUNCHING SOON.
            </h3>
            {/* Body */}
            <p className="text-white/90 text-[length:var(--text-body)] leading-relaxed max-w-sm mx-auto mb-7">
              Skip the custom collection pipeline. We're packaging our first wave of production-ready Indian speech datasets. Fully annotated, certified to the Voqals Quality Standard, and ready to license so you can start training your models immediately.
            </p>
            {/* Email Capture */}
            <div className="w-full mb-5">
              <p className="text-[9px] uppercase tracking-[0.12em] font-bold text-white/40 mb-2 text-center">{notifyDone ? 'You\'re on the list!' : 'Be the first to access them when they go live.'}</p>
              {!notifyDone ? (
              <div className="relative">
                <input
                  type="email"
                  value={notifyEmail}
                  onChange={e => validateNotifyEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleNotify()}
                  placeholder="Enter your work email..."
                  className={`w-full bg-white/5 border rounded-full py-2.5 pl-4 pr-32 text-white placeholder:text-gray-600 focus:outline-none transition-all text-[13px] ${notifyEmailError ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-[#56CFFC]/50 focus:bg-white/10'}`}
                />
                <button onClick={handleNotify} disabled={notifySubmitting || !notifyEmail || !!notifyEmailError} className="absolute right-1 top-1/2 -translate-y-1/2 px-3.5 py-1.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[8px] tracking-[0.12em] uppercase shadow-[0_0_15px_rgba(27,54,255,0.3)] disabled:opacity-40">
                  {notifySubmitting ? '...' : 'Notify Me'}
                </button>
              </div>
              ) : (
              <div className="flex items-center justify-center gap-2 py-2.5">
                <Check className="w-4 h-4 text-[#56CFFC]" />
                <span className="text-white/70 text-[13px]">We'll notify you at launch.</span>
              </div>
              )}
              {notifyEmailError && <p className="text-[8px] text-red-400/80 mt-1 text-center">{notifyEmailError}</p>}
              <p className="text-[8px] text-white/25 mt-1.5 text-center">No spam. Just a single email when datasets are available.</p>
            </div>
            {/* Included With Every Dataset */}
            <div className="w-full">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-white/10"></div>
                <span className="text-[7px] uppercase tracking-[0.2em] font-bold whitespace-nowrap text-white/50">Included With Every Dataset</span>
                <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-white/10"></div>
              </div>
              <div className="grid grid-cols-3 gap-x-2 gap-y-2">
                {[
                  { title: "CLEAN MIX", desc: "Studio Baseline" },
                  { title: "CHAOS MIX", desc: "Simulated Noise" },
                  { title: "ISO TRACKS", desc: "For Overlaps" },
                  { title: "NEAR FIELD", desc: "Intimate Distance" },
                  { title: "FAR FIELD", desc: "Env. Distance" },
                  { title: "METADATA", desc: "Dense JSON" }
                ].map((feature, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <span className="text-[10px] font-display font-bold text-white uppercase tracking-[0.08em] mb-0.5">
                      {feature.title}
                    </span>
                    <span className="text-[7px] uppercase tracking-widest text-[#A4BDE5]/50">
                      {feature.desc}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
          {/* Widget — grows from peek to fullscreen */}
          <motion.div
            ref={widgetRef}
            style={{
              height: mobileWidgetHeight,
              borderRadius: mobileWidgetRadius,
              marginLeft: mobileWidgetMarginLR,
              marginRight: mobileWidgetMarginLR,
              marginBottom: mobileWidgetMarginB,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.02) 100%), rgba(8,8,12,0.75)',
            }}
            className="gradient-border shrink-0 pointer-events-auto backdrop-blur-xl shadow-2xl"
          >
            <div className="w-full h-full flex flex-col relative overflow-hidden">
              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent z-20" />
              {/* Header with title + sliding tab toggle */}
              <div className="px-5 pt-4 pb-3 shrink-0 relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <Database className="w-4 h-4 text-[#56CFFC]" />
                    <h4 className="text-white font-bold tracking-[0.2em] uppercase text-[10px]">Production Datasets</h4>
                  </div>
                  <span className="text-[9px] text-[#A4BDE5]/30 font-mono">{filteredDatasets.length} results</span>
                </div>
                {/* Sliding Tab Toggle — USP3 style */}
                <div ref={mobileTabRef} className="bg-white/[0.04] p-1 rounded-full flex items-center relative border border-white/[0.06]">
                  <motion.div
                    className="absolute top-1 bottom-1 rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] shadow-[0_0_20px_rgba(27,54,255,0.3)]"
                    animate={{ left: mobileTabIndicator.left, width: mobileTabIndicator.width }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                  {filterTabs.map(filter => (
                    <button
                      key={filter}
                      onClick={() => setActiveFilter(filter === 'Training' ? 'Training Use Case' : filter)}
                      className={`relative z-10 flex-1 py-2 rounded-full text-[9px] font-bold tracking-[0.15em] uppercase transition-colors duration-300 ${
                        (filter === 'Training' ? 'Training Use Case' : filter) === activeFilter
                          ? 'text-white'
                          : 'text-white/35 hover:text-white/55'
                      }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>
              {/* Column Headers */}
              <div className="flex items-center px-5 py-2 border-t border-b border-white/[0.04] shrink-0">
                <span className="flex-1 text-[8px] uppercase tracking-[0.2em] text-[#A4BDE5]/30 font-semibold">Dataset</span>
                <span className="w-12 text-center text-[8px] uppercase tracking-[0.2em] text-[#A4BDE5]/30 font-semibold">Spkrs</span>
                <span className="w-10 text-center text-[8px] uppercase tracking-[0.2em] text-[#A4BDE5]/30 font-semibold">Lang</span>
                <span className="w-24 text-right text-[8px] uppercase tracking-[0.2em] text-[#A4BDE5]/30 font-semibold">Status</span>
              </div>
              {/* Dataset Rows */}
              <div className="flex-1 overflow-hidden">
                <AnimatePresence mode="popLayout">
                  {filteredDatasets.map((ds, i) => (
                    <motion.div
                      key={ds.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.97 }}
                      transition={{ delay: i * 0.03, duration: 0.25 }}
                      className="flex items-center px-5 py-3.5 border-b border-white/[0.04] active:bg-white/[0.03] transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <span className="text-white/90 text-[13px] font-medium truncate block">{ds.name}</span>
                      </div>
                      <div className="w-12 text-center">
                        <span className="text-[#56CFFC]/80 text-[12px] font-bold font-mono">{ds.speakers}</span>
                      </div>
                      <div className="w-10 text-center">
                        <span className="text-[#A4BDE5]/60 text-[12px] font-bold font-mono">{ds.languages.length}</span>
                      </div>
                      <div className="w-24 flex items-center justify-end gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${statusDot(ds.status)}`}></span>
                        <span className={`text-[9px] font-medium tracking-wide ${statusColor(ds.status)}`}>{ds.status}</span>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
              {/* Action Buttons */}
              <div className="px-5 pt-4 pb-7 border-t border-white/[0.06] shrink-0">
                <button onClick={() => setDatasetEnquiryOpen(true)} className="w-full py-3 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[9px] tracking-[0.2em] uppercase text-center shadow-[0_0_25px_rgba(27,54,255,0.25)]">
                  Enquire About Datasets
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      {/* Desktop Layout */}
    <section id="datasets-dt" className="hidden md:flex min-h-screen w-full shrink-0 relative flex-col items-center justify-start px-[var(--px-container)] pt-[clamp(60px,5.09vw,88px)] pb-[clamp(22px,1.85vw,32px)]">
    <div className="max-w-[var(--max-content)] w-full mx-auto flex-1 flex flex-col items-center text-center z-10 relative">
      {/* Background Glow */}
      <motion.div
        animate={{ scale: [1, 1.05, 1], opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[500px] bg-[#56CFFC]/10 blur-[150px] rounded-full pointer-events-none"
      />
      {/* Top Content Container */}
      <div className="w-full flex flex-col items-center pt-[clamp(27px,2.31vw,40px)] pb-0 shrink-0">
        
        {/* Centered Content Wrapper */}
        <div className="flex-1 flex flex-col items-center justify-center w-full mt-0">
          {/* Top Label */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false, amount: 0.3 }}
            className="mb-[clamp(27px,2.31vw,40px)] shrink-0"
          >
            <div className="inline-flex items-center gap-[clamp(4px,0.46vw,8px)] px-[clamp(16px,1.39vw,24px)] py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              Ready To Use Datasets
            </div>
          </motion.div>
          {/* Headlines */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false, amount: 0.3 }}
            className="mb-[clamp(27px,2.31vw,40px)] relative flex flex-col items-center shrink-0"
          >
            <h2 className="font-display text-[length:var(--heading-datasets)] font-black text-white leading-[0.85] tracking-tighter uppercase text-center">
              PRODUCTION DATASETS.
            </h2>
            <h3 className="font-display text-[length:var(--heading-datasets)] font-black text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic leading-[0.85] tracking-tighter uppercase mt-[clamp(8px,0.69vw,12px)] text-center">
              LAUNCHING SOON.
            </h3>
          </motion.div>
          {/* Body Copy */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false, amount: 0.3 }}
            className="text-white/90 text-[length:var(--text-body)] max-w-[clamp(520px,45vw,780px)] mx-auto leading-relaxed shrink-0 text-center mb-[clamp(27px,2.31vw,40px)] px-[clamp(11px,0.93vw,16px)]"
          >
            Skip the custom collection pipeline. We're packaging our first wave of production-ready Indian speech datasets. Fully annotated, certified to the Voqals Quality Standard, and ready to license so you can start training your models immediately.
          </motion.p>
          {/* Email Capture */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false, amount: 0.3 }}
            className="flex flex-col items-center shrink-0 w-full max-w-[clamp(303px,25.93vw,448px)] mx-auto"
          >
            <p className="text-[clamp(8px,0.69vw,12px)] uppercase tracking-[0.15em] font-bold text-white/50 mb-[clamp(11px,0.93vw,16px)]">{notifyDone ? 'You\'re on the list!' : 'Be the first to access them when they go live.'}</p>
            {!notifyDone ? (
            <div className="relative group w-full">
              <input
                type="email"
                value={notifyEmail}
                onChange={e => validateNotifyEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleNotify()}
                placeholder="Enter your work email..."
                className={`w-full bg-white/5 border rounded-full py-[clamp(11px,0.93vw,16px)] pl-[clamp(16px,1.39vw,24px)] pr-[clamp(130px,11.11vw,192px)] text-white placeholder:text-gray-600 focus:outline-none transition-all text-[clamp(9px,0.81vw,14px)] ${notifyEmailError ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-[#56CFFC]/50 focus:bg-white/10'}`}
              />
              <button
                onClick={handleNotify}
                disabled={notifySubmitting || !notifyEmail || !!notifyEmailError}
                className="absolute right-[clamp(4px,0.35vw,6px)] top-1/2 -translate-y-1/2 px-[clamp(16px,1.39vw,24px)] py-[clamp(7px,0.58vw,10px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(7px,0.58vw,10px)] tracking-[0.15em] uppercase hover:scale-105 transition-transform shadow-[0_0_20px_rgba(27,54,255,0.3)] disabled:opacity-40"
              >
                {notifySubmitting ? '...' : 'Notify Me at Launch'}
              </button>
            </div>
            ) : (
            <div className="flex items-center justify-center gap-2 py-[clamp(11px,0.93vw,16px)]">
              <Check className="w-[clamp(14px,1.16vw,20px)] h-[clamp(14px,1.16vw,20px)] text-[#56CFFC]" />
              <span className="text-white/70 text-[clamp(9px,0.81vw,14px)]">We'll notify you at launch.</span>
            </div>
            )}
            {notifyEmailError && <p className="text-[clamp(7px,0.58vw,10px)] text-red-400/80 mt-1 text-center">{notifyEmailError}</p>}
            <p className="text-[clamp(7px,0.58vw,10px)] text-white/30 mt-[clamp(8px,0.69vw,12px)]">No spam. Just a single email when datasets are available to license.</p>
          </motion.div>
        </div>
        {/* Dynamic Deliverables List */}
        <motion.div
          ref={deliverablesRef}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          viewport={{ once: false, amount: 0.3 }}
          className="w-full relative pt-0 pb-[clamp(22px,1.85vw,32px)] mt-[clamp(38px,3.24vw,56px)] shrink-0 flex flex-col items-center z-20"
        >
          <div className="flex items-center gap-[clamp(11px,0.93vw,16px)] mb-[clamp(27px,2.31vw,40px)] w-full max-w-4xl px-[clamp(11px,0.93vw,16px)]">
            <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-white/10"></div>
            <span className="text-[clamp(6px,0.52vw,9px)] uppercase tracking-[0.2em] tracking-[0.3em] font-bold text-center">Included With Every Dataset</span>
            <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-white/10"></div>
          </div>
          
          <div className="flex flex-wrap justify-center items-stretch gap-y-[clamp(22px,1.85vw,32px)] w-full max-w-6xl px-[clamp(11px,0.93vw,16px)]">
            {[
              { title: "CLEAN MIX", desc: "Studio Baseline" },
              { title: "CHAOS MIX", desc: "Simulated Noise" },
              { title: "ISO TRACKS", desc: "For Overlaps" },
              { title: "NEAR FIELD", desc: "Intimate Distance" },
              { title: "FAR FIELD", desc: "Env. Distance" },
              { title: "METADATA", desc: "Dense JSON" }
            ].map((feature, index, array) => (
              <React.Fragment key={index}>
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="flex flex-col items-center px-[clamp(16px,1.39vw,24px)] group w-auto"
                >
                  <span className="text-[clamp(9px,0.81vw,14px)] font-display font-bold text-white uppercase tracking-[0.1em] tracking-[0.2em] mb-[clamp(3px,0.23vw,4px)] group-hover:text-[#56CFFC] transition-colors text-center">
                    {feature.title}
                  </span>
                  <span className="text-[clamp(6px,0.52vw,9px)] uppercase tracking-wider tracking-widest text-center">
                    {feature.desc}
                  </span>
                </motion.div>
                {index < array.length - 1 && index !== 2 && (
                  <div className="block w-[1px] bg-gradient-to-b from-transparent via-white/10 to-transparent" />
                )}
              </React.Fragment>
            ))}
          </div>
        </motion.div>
      </div>
      {/* Dataset Widget */}
      <motion.div
        id="datasets-widget"
        ref={widgetRef}
        layout
        initial={{ opacity: 1, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, layout: { duration: 0.5, ease: "easeOut" } }}
        className="w-full relative flex flex-col items-center justify-center transition-all duration-700 pb-0 mt-[clamp(32px,2.78vw,48px)] z-10 px-[clamp(11px,0.93vw,16px)] shrink-0 scroll-mt-[clamp(90px,7.64vw,132px)]"
      >
        {/* Subtle Animated Glow Behind Widget */}
        <motion.div
          animate={{ opacity: [0.4, 0.7, 0.4], scale: [0.98, 1.02, 0.98] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 max-w-[var(--max-content)] mx-auto bg-[#1B36FF]/40 blur-[100px] rounded-[3rem] pointer-events-none"
        />
        
        <motion.div 
          layout
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="gradient-border w-full h-full max-w-6xl mx-auto bg-[#050505]/60 backdrop-blur-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col relative rounded-2xl overflow-hidden"
        >
          {/* Subtle top highlight */}
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent z-20" />
          {/* Subtle background glow */}
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#1B36FF]/10 blur-[100px] rounded-full pointer-events-none" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#56CFFC]/10 blur-[100px] rounded-full pointer-events-none" />
          {/* Header & Filters */}
          <div className="p-[clamp(12px,1.39vw,24px)] border-b border-white/10 flex flex-row justify-between items-center gap-4 bg-white/[0.02] shrink-0 relative z-10">
            <div className="flex items-center gap-[clamp(8px,0.69vw,12px)]">
              <Database className="w-[clamp(14px,1.16vw,20px)] h-[clamp(14px,1.16vw,20px)] text-[#56CFFC]" />
              <h4 className="text-white font-bold tracking-widest uppercase text-[clamp(9px,0.81vw,14px)]">Production Datasets</h4>
            </div>
            
            <div className="flex flex-row items-center gap-[clamp(11px,0.93vw,16px)] w-auto">
              <div className="flex bg-white/5 p-[clamp(4px,0.35vw,6px)] rounded-[clamp(8px,0.69vw,12px)] border border-white/10">
                {['All', 'Personas', 'Training Use Case'].map(filter => (
                  <button
                    key={filter}
                    onClick={() => setActiveFilter(filter)}
                    className={`px-[clamp(12px,1.39vw,24px)] py-[clamp(7px,0.58vw,10px)] rounded-lg text-[clamp(10px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase transition-all duration-300 ${
                      activeFilter === filter 
                        ? 'bg-[#1B36FF]/40 text-white shadow-[0_0_15px_rgba(27,54,255,0.3)]' 
                        : 'text-[#A4BDE5]/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
              
              <button onClick={() => setDatasetEnquiryOpen(true)} className="px-[clamp(16px,1.39vw,24px)] py-[clamp(8px,0.69vw,12px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(7px,0.58vw,10px)] tracking-[0.2em] uppercase transition-all duration-300 hover:scale-105 shadow-[0_0_20px_rgba(27,54,255,0.3)] whitespace-nowrap">
                Enquire About Datasets
              </button>
            </div>
          </div>
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-[clamp(11px,0.93vw,16px)] py-[clamp(11px,0.93vw,16px)] px-[clamp(12px,1.39vw,24px)] border-b border-white/10 bg-white/[0.01] text-[clamp(7px,0.58vw,10px)] uppercase tracking-widest font-bold text-left shrink-0 relative z-10">
            <div className="col-span-4">Dataset</div>
            <div className="col-span-2">Speakers</div>
            <div className="col-span-3">Languages</div>
            <div className="col-span-3 text-right">Status</div>
          </div>
          {/* Table Body */}
          <div className="relative flex-1 overflow-y-auto no-scrollbar">
            <div className="flex flex-col pb-4">
              <AnimatePresence mode="popLayout">
                {filteredDatasets.map((ds, i) => (
                  <motion.div 
                    key={ds.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ delay: i * 0.05, duration: 0.3, ease: "easeOut" }}
                    className="grid grid-cols-12 gap-[clamp(11px,0.93vw,16px)] py-[clamp(14px,1.16vw,20px)] px-[clamp(12px,1.39vw,24px)] border-b border-white/5 hover:bg-gradient-to-r hover:from-white/[0.05] hover:to-transparent transition-all duration-300 items-center text-[clamp(9px,0.81vw,14px)] text-left group cursor-pointer relative"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#56CFFC] scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-center" />
                    <div className="col-span-4 font-medium text-white flex flex-col gap-[clamp(3px,0.23vw,4px)]">
                      <div className="flex items-center gap-[clamp(5px,0.46vw,8px)]">
                        <div className="w-[clamp(4px,0.35vw,6px)] h-[clamp(4px,0.35vw,6px)] rounded-full bg-[#56CFFC] opacity-0 group-hover:opacity-100 transition-opacity" />
                        <span className="truncate text-[clamp(11px,0.93vw,16px)]">{ds.name}</span>
                      </div>
                      <span className="text-[clamp(7px,0.58vw,10px)] uppercase tracking-wider text-white/40 ml-[clamp(9px,0.81vw,14px)]">{ds.category}</span>
                    </div>

                    <div className="col-span-2 font-mono text-[#56CFFC] text-[clamp(10px,0.81vw,14px)] font-bold">
                      {ds.speakers}
                    </div>

                    <div className="col-span-3 font-mono text-[#A4BDE5]/80 text-[clamp(10px,0.81vw,14px)]">
                      {ds.languages.length}
                    </div>

                    <div className="col-span-3 flex items-center justify-end gap-[clamp(5px,0.46vw,8px)]">
                      <span className={`w-[clamp(5px,0.35vw,6px)] h-[clamp(5px,0.35vw,6px)] rounded-full ${statusDot(ds.status)}`} />
                      <span className={`text-[clamp(8px,0.69vw,12px)] font-medium tracking-wide ${statusColor(ds.status)}`}>{ds.status}</span>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
    </section>
    <DatasetEnquiryOverlay isOpen={datasetEnquiryOpen} onClose={() => setDatasetEnquiryOpen(false)} />
    </>
  );
};
/* ── Why India, Why Voqals — shared content, mobile scroll-driven widget reveal ── */
const WhyIndiaSection = ({ whyIndiaPlaying, setWhyIndiaPlaying, whyIndiaProgress, whyIndiaDuration }: {
  whyIndiaPlaying: boolean;
  setWhyIndiaPlaying: (v: boolean) => void;
  whyIndiaProgress: number;
  whyIndiaDuration: number;
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollYProgress = useMotionValue(0);
  // Mobile scroll handler — same pattern as USP1
  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    const section = sectionRef.current;
    if (!container || !section) return;
    const handleScroll = () => {
      // Only track on mobile
      if (window.innerWidth >= 768) { scrollYProgress.set(0); return; }
      let sectionTop = 0;
      let el: HTMLElement | null = section;
      while (el && el !== container) {
        sectionTop += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
      }
      const sectionHeight = section.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      const start = sectionTop;
      const end = sectionTop + sectionHeight - containerHeight;
      const progress = Math.max(0, Math.min(1, (scrollTop - start) / (end - start)));
      scrollYProgress.set(progress);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  // Mobile scroll-driven transforms
  // Widget grows from bottom upward, overlaying the text
  // Paragraphs + trust line blur slightly as widget covers them
  // Pill + heading stay crisp and visible
  // Paragraphs/trust line get slight blur as widget overlays
  const bodyBlurAmount = useTransform(scrollYProgress, [0.1, 0.45], [0, 4]);
  const bodyBlurFilter = useMotionTemplate`blur(${bodyBlurAmount}px)`;
  const bodyOpacity = useTransform(scrollYProgress, [0.1, 0.45], [1, 0.5]);
  // Widget details (below play bar) — blurred initially, revealed on scroll
  const widgetDetailsBlurAmount = useTransform(scrollYProgress, [0.1, 0.5], [6, 0]);
  const widgetDetailsBlurFilter = useMotionTemplate`blur(${widgetDetailsBlurAmount}px)`;
  const widgetDetailsOpacity = useTransform(scrollYProgress, [0.1, 0.5], [0.4, 1]);
  // Widget height grows from peek (play bar only ~150px) to fit content
  const widgetHeight = useTransform(scrollYProgress, (v) => {
    const p = Math.min(1, Math.max(0, v / 0.65));
    const startH = 150; // just the play bar area
    const endH = 495; // fits all widget content including persona
    return startH + (endH - startH) * p;
  });
  // Scroll indicator opacity — visible only in closed/near-closed state
  const scrollIndicatorOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  // Waveform + play bar — shared between mobile and desktop
  const renderPlayBar = () => (
    <>
      {/* Top highlight line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      {/* File label + timecode */}
      <div className="flex items-center justify-between mb-[clamp(8px,0.6vw,12px)]">
        <span className="text-[clamp(9px,0.6vw,13px)] font-mono text-white/40 uppercase tracking-[0.1em]">Code-Switched Audio · 4 Languages</span>
        <span className="text-[clamp(9px,0.6vw,13px)] font-mono text-white/25">
          {`${Math.floor(whyIndiaProgress * whyIndiaDuration / 60).toString().padStart(2, '0')}:${Math.floor(whyIndiaProgress * whyIndiaDuration % 60).toString().padStart(2, '0')}`} / {`${Math.floor(whyIndiaDuration / 60).toString().padStart(2, '0')}:${Math.floor(whyIndiaDuration % 60).toString().padStart(2, '0')}`}
        </span>
      </div>
      {/* Waveform + Play button row */}
      <div className="flex items-stretch gap-[clamp(12px,1vw,20px)] p-[clamp(12px,1vw,20px)] rounded-[clamp(12px,1vw,20px)] bg-white/[0.03] border border-white/[0.06] mb-[clamp(16px,1.4vw,28px)]">
        <div className="flex-1 flex flex-col gap-[clamp(4px,0.35vw,7px)] justify-center min-w-0">
          <div className="flex items-center gap-[clamp(8px,0.6vw,12px)]">
            <span className="text-[clamp(7px,0.45vw,10px)] font-mono text-white/25 uppercase tracking-[0.1em] w-[clamp(28px,2vw,40px)] shrink-0">User</span>
            <div className="flex-1 h-[clamp(20px,1.8vw,36px)] flex items-center gap-[clamp(1px,0.1vw,2px)] relative">
              {Array.from({ length: 64 }).map((_, i) => {
                const heights = [25, 45, 65, 85, 55, 75, 40, 92, 50, 70, 35, 80, 60, 45, 85, 30, 65, 75, 50, 92, 40, 55, 80, 70, 35, 88, 50, 65, 75, 30, 60, 92, 45, 70, 40, 80, 55, 88, 35, 65, 50, 75, 60, 92, 30, 45, 70, 40, 82, 55, 68, 42, 90, 48, 72, 38, 85, 58, 78, 32, 62, 88, 52, 75];
                // Language segments mapped to normalized positions (0-1)
                const segments = [
                  { start: 0.092, end: 0.249, color: '#FF7F6B' },   // Anna — Tamil
                  { start: 0.249, end: 0.333, color: '#F59E0B' }, // don — Marathi
                  { start: 0.333, end: 0.385, color: '#56CFFC' }, // cup — English
                  { start: 0.385, end: 0.468, color: '#A78BFA' }, // chai — Hindi
                  { start: 0.468, end: 0.580, color: '#F59E0B' }, // dya na — Marathi
                  { start: 0.655, end: 0.695, color: '#A78BFA' }, // Aur — Hindi
                  { start: 0.695, end: 0.793, color: '#56CFFC' }, // sugar — English
                  { start: 0.793, end: 0.891, color: '#A78BFA' }, // kam — Hindi
                ];
                const barPos = i / 64;
                const seg = segments.find(s => barPos >= s.start && barPos < s.end);
                const color = seg ? seg.color : '#888888';
                const isSilence = !seg;
                const isPlayed = barPos <= whyIndiaProgress;
                return (
                  <div
                    key={i}
                    className="rounded-full flex-1"
                    style={{
                      backgroundColor: color,
                      opacity: isSilence ? 0.1 : (isPlayed ? 0.9 : 0.25),
                      height: isSilence ? '15%' : `${heights[i % heights.length]}%`,
                    }}
                  />
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-[clamp(8px,0.6vw,12px)]">
            <span className="w-[clamp(28px,2vw,40px)] shrink-0" />
            <div className="flex-1 h-[1.5px] rounded-full bg-white/[0.06] relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-white/60 rounded-full transition-all duration-75"
                style={{ width: `${whyIndiaProgress * 100}%` }}
              />
            </div>
          </div>
        </div>
        <button
          aria-label={whyIndiaPlaying ? "Pause audio" : "Play audio"}
          onClick={() => setWhyIndiaPlaying(!whyIndiaPlaying)}
          className="w-[clamp(40px,3.2vw,64px)] shrink-0 rounded-full bg-gradient-to-br from-[#56CFFC] to-[#1B36FF] flex items-center justify-center self-center aspect-square shadow-[0_0_20px_rgba(27,54,255,0.3)] hover:shadow-[0_0_30px_rgba(27,54,255,0.5)] transition-shadow cursor-pointer"
        >
          {whyIndiaPlaying ? (
            <svg viewBox="0 0 24 24" fill="white" className="w-[clamp(14px,1.1vw,22px)] h-[clamp(14px,1.1vw,22px)]">
              <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="white" className="w-[clamp(14px,1.1vw,22px)] h-[clamp(14px,1.1vw,22px)] ml-[2px]">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
      </div>
    </>
  );
  // Widget details: legend, pills, callout — shared content
  const renderWidgetDetails = () => (
    <>
      {/* Divider */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent mb-[clamp(16px,1.4vw,28px)]" />
      {/* Language legend */}
      <div className="flex items-center justify-center gap-[clamp(14px,1.2vw,24px)] mb-[clamp(12px,1vw,20px)]">
        {[
          { color: '#FF7F6B', label: 'Tamil' },
          { color: '#F59E0B', label: 'Marathi' },
          { color: '#56CFFC', label: 'English' },
          { color: '#A78BFA', label: 'Hindi' },
        ].map((lang, i) => (
          <div key={i} className="flex items-center gap-[clamp(4px,0.3vw,6px)]">
            <div className="w-[clamp(5px,0.4vw,8px)] h-[clamp(5px,0.4vw,8px)] rounded-full" style={{ backgroundColor: lang.color, opacity: 0.9, boxShadow: `0 0 6px ${lang.color}50` }} />
            <span className="text-[10px] md:text-[clamp(8px,0.5vw,11px)] font-mono uppercase tracking-[0.05em]" style={{ color: lang.color, opacity: 0.7 }}>{lang.label}</span>
          </div>
        ))}
      </div>
      {/* Sentence as flowing pills */}
      <div className="flex flex-wrap gap-[clamp(4px,0.4vw,10px)] mb-[clamp(16px,1.4vw,28px)] px-1">
        {[
          { text: '"Anna,"', lang: 'Tamil', color: '#FF7F6B', start: 0.092, end: 0.249 },
          { text: '"don"', lang: 'Marathi', color: '#F59E0B', start: 0.249, end: 0.333 },
          { text: '"cup"', lang: 'English', color: '#56CFFC', start: 0.333, end: 0.385 },
          { text: '"chai"', lang: 'Hindi', color: '#A78BFA', start: 0.385, end: 0.468 },
          { text: '"dya na."', lang: 'Marathi', color: '#F59E0B', start: 0.468, end: 0.580 },
          { text: '"Aur"', lang: 'Hindi', color: '#A78BFA', start: 0.655, end: 0.695 },
          { text: '"sugar"', lang: 'English', color: '#56CFFC', start: 0.695, end: 0.793 },
          { text: '"kam."', lang: 'Hindi', color: '#A78BFA', start: 0.793, end: 0.891 },
        ].map((seg, i) => {
          const isActive = whyIndiaPlaying && whyIndiaProgress >= seg.start && whyIndiaProgress < seg.end;
          const isPast = whyIndiaProgress >= seg.end;
          return (
            <div
              key={i}
              className="transition-all duration-200"
              style={{ opacity: isActive ? 1 : isPast ? 0.7 : (whyIndiaProgress > 0 ? 0.35 : 0.7) }}
            >
              <div
                className="px-[clamp(10px,0.8vw,20px)] py-[clamp(5px,0.45vw,12px)] rounded-[clamp(8px,0.6vw,12px)] text-[12px] md:text-[clamp(10px,0.65vw,14px)] font-medium font-mono transition-all duration-200 whitespace-nowrap"
                style={{
                  color: seg.color,
                  backgroundColor: isActive ? `${seg.color}20` : `${seg.color}10`,
                  border: `1px solid ${isActive ? seg.color + '50' : seg.color + '25'}`,
                  boxShadow: isActive ? `0 0 20px ${seg.color}25` : 'none',
                  transform: isActive ? 'scale(1.05)' : 'scale(1)',
                }}
              >
                {seg.text}
              </div>
            </div>
          );
        })}
      </div>
      {/* English translation */}
      <p className="text-[14px] md:text-[clamp(13px,0.85vw,15px)] text-white/35 italic mb-[clamp(12px,1vw,20px)] leading-relaxed">
        "Big brother, give me two cups of tea. And less sugar."
      </p>
      {/* Persona + Bottom callout */}
      <div className="pt-[clamp(12px,1vw,20px)] border-t border-white/5 flex flex-col flex-1">
        {/* Desktop: 2-column grid persona */}
        <div className="hidden md:grid grid-cols-2 gap-x-[clamp(16px,1.4vw,28px)] gap-y-[clamp(6px,0.5vw,10px)] mb-[clamp(4px,0.3vw,8px)] text-[clamp(9px,0.6vw,12px)]">
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[clamp(7px,0.45vw,9px)] block mb-[2px]">Persona</span>
            <span className="text-white/60">GenPop · Mumbai, Urban · 25 yrs</span>
          </div>
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[clamp(7px,0.45vw,9px)] block mb-[2px]">Native</span>
            <span style={{ color: '#F59E0B', opacity: 0.7 }}>Marathi</span>
          </div>
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[clamp(7px,0.45vw,9px)] block mb-[2px]">Secondary</span>
            <span style={{ color: '#A78BFA', opacity: 0.7 }}>Hindi</span>
            <span className="text-white/10"> · </span>
            <span style={{ color: '#56CFFC', opacity: 0.7 }}>English</span>
          </div>
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[clamp(7px,0.45vw,9px)] block mb-[2px]">Context</span>
            <span className="text-white/45">Uses <span style={{ color: '#FF7F6B', opacity: 0.8 }}>Tamil</span> — listener is Tamil</span>
          </div>
        </div>
        {/* Mobile: two-column compact layout */}
        <div className="md:hidden grid grid-cols-2 gap-x-4 gap-y-3 text-[11px] mb-1">
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[9px] block mb-0.5">Persona</span>
            <span className="text-white/60">GenPop · Mumbai · 25 yrs</span>
          </div>
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[9px] block mb-0.5">Native</span>
            <span style={{ color: '#F59E0B', opacity: 0.7 }}>Marathi</span>
          </div>
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[9px] block mb-0.5">Secondary</span>
            <span style={{ color: '#A78BFA', opacity: 0.7 }}>Hindi</span>
            <span className="text-white/10"> · </span>
            <span style={{ color: '#56CFFC', opacity: 0.7 }}>English</span>
          </div>
          <div>
            <span className="font-bold text-white/20 uppercase tracking-widest text-[9px] block mb-0.5">Context</span>
            <span className="text-white/45">Uses <span style={{ color: '#FF7F6B', opacity: 0.8 }}>Tamil</span> — listener is Tamil</span>
          </div>
        </div>
        {/* Bottom callout — always at bottom */}
        <div className="flex items-center justify-end mt-auto">
          <div className="flex items-center gap-[clamp(4px,0.35vw,8px)]">
            <span className="text-[13px] md:text-[clamp(10px,0.7vw,15px)] font-display font-black text-white/90 uppercase tracking-[0.05em]">4 languages.</span>
            <span className="text-[13px] md:text-[clamp(10px,0.7vw,15px)] font-display font-black text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#A78BFA] italic uppercase tracking-[0.05em]">1 breath.</span>
          </div>
        </div>
      </div>
    </>
  );
  const widgetCardStyle = { background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' };
  // Shared text content
  const renderTextContent = () => (
    <>
      <h2 className="font-display text-[clamp(32px,8vw,52px)] md:text-[clamp(32px,2.8vw,68px)] font-black tracking-tighter leading-[1.05] text-[#EEEEEE] uppercase mb-[clamp(16px,1.5vw,28px)]">
        INDIAN SPEECH IS CHAOTIC.<br className="hidden md:block" />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pb-1 pr-2">AND WE LIVE IN IT.</span>
      </h2>
      <p className="text-white/90 text-[length:var(--text-body)] max-w-sm md:max-w-full leading-relaxed mb-[clamp(12px,1vw,20px)]">
        Hindi into English into regional dialects, mid-sentence, mid-thought. When India speaks four languages in a single breath, bilingual models break. We don't just study this linguistic chaos — we grew up in it.
      </p>
      <p className="text-white/90 text-[length:var(--text-body)] max-w-sm md:max-w-full leading-relaxed mb-[clamp(16px,1.4vw,28px)]">
        Our team lives, breathes, and engineers the true, unfiltered voice of the subcontinent.
      </p>
      {/* Closing line — same style as hero trust line */}
      <div className="flex items-center gap-3 md:gap-4">
        <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#56CFFC] to-[#1B36FF] shrink-0" />
        <p className="text-[10px] md:text-[clamp(10px,0.65vw,16px)] tracking-[0.15em] font-semibold text-white/50 leading-relaxed uppercase">
          The only way to build{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">AI that understands India</span>,{' '}
          is to have{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">India build it.</span>
        </p>
        <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#1B36FF] to-[#56CFFC] shrink-0 md:hidden" />
      </div>
    </>
  );
  return (
    <>
      {/* ── Mobile: scroll-driven widget reveal ── */}
      <div ref={sectionRef} id="why-india-mobile" className="md:hidden h-[200dvh] w-full relative shrink-0">
        <div className="sticky top-0 h-[100dvh] w-full overflow-hidden">
          {/* Text content — vertically centered between nav and widget peek */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-[var(--px-container)] pt-[60px] pb-[170px] pointer-events-auto">
            {/* Pill — always crisp */}
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)] mb-6 z-10">
              Why India, Why Voqals
            </div>
            {/* Heading — always crisp */}
            <h2 className="font-display text-[clamp(22px,7.07vw,52px)] font-black tracking-tighter leading-[1.05] text-[#EEEEEE] uppercase mb-6 z-10">
              INDIAN SPEECH IS CHAOTIC.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pb-1 pr-2">AND WE LIVE IN IT.</span>
            </h2>
            {/* Body text + trust line — blurs slightly as widget overlays */}
            <motion.div
              style={{ opacity: bodyOpacity, filter: bodyBlurFilter }}
              className="flex flex-col items-center"
            >
              <p className="text-white/90 text-[length:var(--text-body)] max-w-sm leading-relaxed mb-4">
                Hindi into English into regional dialects, mid-sentence, mid-thought. When India speaks four languages in a single breath, bilingual models break. We don't just study this linguistic chaos — we grew up in it.
              </p>
              <p className="text-white/90 text-[length:var(--text-body)] max-w-sm leading-relaxed mb-6">
                Our team lives, breathes, and engineers the true, unfiltered voice of the subcontinent.
              </p>
              <div className="flex items-center gap-3">
                <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#56CFFC] to-[#1B36FF] shrink-0" />
                <p className="text-[10px] tracking-[0.15em] font-semibold text-white/50 leading-relaxed uppercase">
                  The only way to build{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">AI that understands India</span>,{' '}
                  is to have{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">India build it.</span>
                </p>
                <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#1B36FF] to-[#56CFFC] shrink-0" />
              </div>
            </motion.div>
          </div>
          {/* Widget — positioned at bottom, grows upward on scroll */}
          <motion.div
            style={{ height: widgetHeight }}
            className="absolute bottom-4 left-4 right-4 overflow-hidden rounded-2xl pointer-events-auto z-20"
          >
            {/* Scroll-up indicator — bouncing chevron at bottom, fades on scroll */}
            <motion.div
              style={{ opacity: scrollIndicatorOpacity }}
              className="absolute bottom-0 left-0 right-0 z-30 flex flex-col items-center pb-2.5 pointer-events-none"
            >
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                className="flex flex-col items-center gap-1"
              >
                <svg width="22" height="10" viewBox="0 0 22 10" fill="none" className="opacity-40">
                  <path d="M2 8L11 2L20 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <svg width="22" height="10" viewBox="0 0 22 10" fill="none" className="opacity-20 -mt-1.5">
                  <path d="M2 8L11 2L20 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.div>
            </motion.div>
            <div className="gradient-border rounded-2xl backdrop-blur-xl shadow-2xl relative h-full" style={widgetCardStyle}>
              <div className="px-5 py-5 flex flex-col overflow-hidden rounded-[inherit] h-full">
                {renderPlayBar()}
                {/* Details — blurred initially, revealed on scroll */}
                <motion.div
                  className="flex flex-col flex-1 overflow-hidden"
                  style={{ filter: widgetDetailsBlurFilter, opacity: widgetDetailsOpacity }}
                >
                  {renderWidgetDetails()}
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      {/* ── Desktop: static layout ── */}
      <section id="why-india" className="hidden md:flex min-h-[100dvh] w-full shrink-0 relative flex-col items-center justify-center pt-20 md:pt-24 pb-8 overflow-hidden">
        {/* Background Glows */}
        <div className="absolute top-1/3 left-1/4 w-[50%] h-[50%] bg-[#1B36FF]/6 blur-[150px] rounded-full pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[40%] h-[40%] bg-[#56CFFC]/4 blur-[120px] rounded-full pointer-events-none" />
        <div className="max-w-[var(--max-content)] w-full mx-auto z-10 flex flex-col px-[var(--px-container)]">
          {/* Pill — above the two-column row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: false, amount: 0.3 }}
            className="flex justify-start mb-[clamp(16px,1.4vw,28px)]"
          >
            <div className="inline-flex items-center gap-[clamp(4px,0.46vw,8px)] px-[clamp(16px,1.39vw,24px)] py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
              Why India, Why Voqals
            </div>
          </motion.div>
          {/* Two-column row — vertically centered */}
          <div className="flex flex-row items-center gap-[clamp(32px,3vw,64px)]">
            {/* Left Column — Copy */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: false, amount: 0.3 }}
              className="w-[45%] min-w-0 flex flex-col items-start text-left"
            >
              {renderTextContent()}
            </motion.div>
            {/* Right Column — Code-Switching Visualizer */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
              viewport={{ once: false, amount: 0.3 }}
              className="flex-1 flex flex-col items-end min-w-0"
            >
              <div className="w-full relative">
                <div className="gradient-border rounded-[clamp(20px,1.85vw,32px)] backdrop-blur-xl shadow-2xl relative" style={widgetCardStyle}>
                  <div className="px-[clamp(20px,1.6vw,32px)] py-[clamp(20px,1.8vw,36px)] flex flex-col overflow-hidden rounded-[inherit]">
                    {renderPlayBar()}
                    {renderWidgetDetails()}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};
const languages = [
  "Telugu", "Bengali", "Marathi", "Gujarati", "Kannada", "Malayalam",
  "Punjabi", "Tamil", "Odia", "Urdu", "Bhojpuri", "Maithili",
  "Konkani", "Sindhi", "Dogri", "Santali", "Kashmiri"
];
const HeroSection = React.memo(() => (
  <section id="hero" className="min-h-[100dvh] w-full shrink-0 relative flex flex-col pt-20 md:pt-24 z-20 overflow-hidden will-change-transform">
    {/* Subtle Background Glow */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-gradient-to-br from-[#1B36FF]/10 to-[#56CFFC]/5 blur-[120px] rounded-full pointer-events-none animate-pulse-slow" />
    <main className="flex-1 flex flex-col md:flex-row items-center justify-center max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] py-0 gap-8 md:gap-[var(--gap-hero)] pointer-events-auto relative z-10">
      {/* Left Column: Typography & CTAs */}
      <div className="flex flex-col items-center text-center md:items-start md:text-left justify-center w-full md:w-1/2 max-w-none md:max-w-[clamp(440px,42vw,960px)] shrink-0 relative">
        {/* Pill - positioned above the centered content on desktop */}
        <div className="mb-6 md:absolute md:-top-[clamp(48px,3.5vw,86px)] md:left-0 md:mb-0 animate-hero-fade-in">
          <div className="inline-flex items-center gap-2 px-5 py-2 md:gap-[clamp(4px,0.46vw,8px)] md:px-[clamp(16px,1.39vw,24px)] md:py-[clamp(7px,0.58vw,10px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)]">
            Ground Truth For Indian Speech
          </div>
        </div>
        <h1 className="font-display text-[clamp(28px,9.2vw,56px)] md:text-[clamp(36px,4vw,84px)] font-black tracking-tighter leading-[1.05] mb-6 md:mb-[clamp(12px,1.2vw,28px)] w-full uppercase">
          INDIAN SPEECH DATA.<br />
          <span className="font-black text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pb-1 pr-2">BUILT RIGHT.</span>
        </h1>
        <p className="text-white/90 text-[length:var(--text-body)] max-w-sm md:max-w-full mb-7 md:mb-[clamp(12px,1.4vw,28px)] leading-relaxed">
          Power your generative and conversational AI with expertly directed, code-switched Indian speech. We deliver SFT-ready, richly annotated datasets captured in <span className="text-white font-bold">Voqals certified studios</span> with uncompromising precision.
        </p>
        {/* Trust Line */}
        <div className="mb-7 md:mb-[clamp(14px,1.6vw,32px)] w-full md:w-auto animate-hero-fade-in [animation-delay:0.3s]">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#56CFFC] to-[#1B36FF] shrink-0" />
            <p className="text-[10px] md:text-[clamp(10px,0.65vw,16px)] tracking-[0.15em] font-semibold text-white/50 leading-relaxed uppercase">
              <Link
                to="/audio-company-india/"
                className="underline decoration-white/20 underline-offset-4 hover:decoration-[#56CFFC] hover:text-white/80 transition-colors"
              >
                From the team behind
              </Link>{' '}
              producing speech data powering AI used by{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] font-bold">hundreds of millions</span>{' '}
              worldwide.
            </p>
            <div className="h-8 w-[2px] rounded-full bg-gradient-to-b from-[#1B36FF] to-[#56CFFC] shrink-0 md:hidden" />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4 md:gap-5 w-full md:w-auto animate-hero-fade-in [animation-delay:0.4s]">
          <Link
            to="/custom-data-collection/"
            className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,48px)] py-[clamp(14px,1.16vw,24px)] bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[clamp(10px,0.69vw,14px)] tracking-[0.2em] uppercase transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(27,54,255,0.3)] whitespace-nowrap"
          >
            <span className="relative z-10 flex items-center gap-3 text-center whitespace-nowrap">
              Custom Data Collection
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
          <button
            onClick={() => scrollToId('why-india')}
            className="group relative w-full sm:w-auto px-[clamp(28px,2.31vw,48px)] py-[clamp(14px,1.16vw,24px)] rounded-full border border-white/20 text-white text-[clamp(10px,0.69vw,14px)] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all hover:bg-white/5 hover:text-white hover:border-white/40 flex items-center justify-center whitespace-nowrap"
          >
            <span className="relative z-10 flex items-center gap-2 text-center whitespace-nowrap">See Why Voqals <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
          </button>
        </div>
      </div>
      {/* Right Column: Rotating USP Cards */}
      <div className="hidden md:block w-1/2 perspective-1000 translate-y-[25px] animate-hero-fade-in [animation-delay:0.4s]">
        <RotatingUSPCards />
      </div>
    </main>
    {/* Language Marquee */}
    <div className="relative z-20 w-full pt-3 pb-6 md:pb-8 py-6 overflow-hidden">
      <div className="flex w-max animate-marquee">
        {[1, 2].map((set) => (
          <div key={set} className="flex items-center">
            {languages.map((lang, index) => (
              <div key={`${set}-${index}`} className="flex items-center group px-8 md:px-[clamp(40px,2.5vw,95px)]">
                <span className="text-[10px] md:text-[clamp(9px,0.65vw,14px)] font-display font-medium text-white/90 uppercase tracking-[0.3em] md:tracking-[0.4em] group-hover:text-white transition-colors duration-500 cursor-default">
                  {lang}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </section>
));
export default function App() {
  const containerRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ container: containerRef });
  const [activeNav, setActiveNav] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProjectDrawerOpen, setIsProjectDrawerOpen] = useState(false);
  const [isStudioFormOpen, setIsStudioFormOpen] = useState(false);
  const navigate = useNavigate();
  const scrollToSection = (id: string) => {
    setIsMobileMenuOpen(false);
    setTimeout(() => scrollToId(id), 400);
  };

  // Handle hash-based navigation (e.g. /#why-india from the landing page navbar).
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    // Wait for layout, then scroll
    const t = setTimeout(() => {
      scrollToId(hash);
      // Clean hash so refresh doesn't re-scroll awkwardly
      history.replaceState(null, '', window.location.pathname);
    }, 150);
    return () => clearTimeout(t);
  }, []);
  // Why India audio player state
  const [whyIndiaPlaying, setWhyIndiaPlaying] = useState(false);
  const [whyIndiaProgress, setWhyIndiaProgress] = useState(0);
  const [whyIndiaDuration, setWhyIndiaDuration] = useState(0);
  const whyIndiaAudioRef = useRef<HTMLAudioElement | null>(null);
  const whyIndiaRafRef = useRef<number>(0);
  const whyIndiaSectionRef = useRef<HTMLDivElement>(null);
  const [whyIndiaVisible, setWhyIndiaVisible] = useState(false);

  // Lazy-load audio when Why India section enters viewport
  useEffect(() => {
    const el = whyIndiaSectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setWhyIndiaVisible(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Create audio element once section is visible
  useEffect(() => {
    if (!whyIndiaVisible) return;
    const audio = new Audio('/Audio/4langs.wav');
    whyIndiaAudioRef.current = audio;
    audio.addEventListener('loadedmetadata', () => setWhyIndiaDuration(audio.duration));
    audio.addEventListener('ended', () => {
      setWhyIndiaPlaying(false);
      setWhyIndiaProgress(0);
    });
    return () => { audio.pause(); audio.src = ''; };
  }, [whyIndiaVisible]);

  // Sync playback with state
  useEffect(() => {
    const audio = whyIndiaAudioRef.current;
    if (!audio) return;
    if (whyIndiaPlaying) {
      audio.play().catch(() => {});
      const tick = () => {
        if (audio.duration) setWhyIndiaProgress(audio.currentTime / audio.duration);
        whyIndiaRafRef.current = requestAnimationFrame(tick);
      };
      whyIndiaRafRef.current = requestAnimationFrame(tick);
    } else {
      audio.pause();
      cancelAnimationFrame(whyIndiaRafRef.current);
    }
    return () => cancelAnimationFrame(whyIndiaRafRef.current);
  }, [whyIndiaPlaying]);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            startTransition(() => {
              if (['overview', 'usp1', 'usp2', 'usp3'].includes(id)) {
                setActiveNav('difference');
              } else if (id === 'why-india' || id === 'why-india-mobile') {
                setActiveNav('why-india');
              } else if (id === 'hero') {
                setActiveNav('');
              } else if (id === 'datasets-dt') {
                setActiveNav('datasets');
              } else {
                setActiveNav(id);
              }
            });
          }
        });
      },
      { threshold: 0.3 }
    );
    const sections = ['hero', 'why-india', 'why-india-mobile', 'overview', 'usp1', 'usp2', 'usp3', 'process', 'datasets', 'datasets-dt'];
    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

// Parallax transforms for the background "O"s
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 100]);
  
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  // Tighter spring for more responsive spotlight
  const springX = useSpring(mouseX, { stiffness: 100, damping: 30 });
  const springY = useSpring(mouseY, { stiffness: 100, damping: 30 });
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left);
    mouseY.set(e.clientY - rect.top);
  };
  return (
    <>
    <div
      id="main-scroll-container"
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className="h-[100dvh] w-full overflow-x-hidden overflow-y-scroll overscroll-y-contain  no-scrollbar bg-[#000000] text-[#EEEEEE] selection:bg-[#1B36FF]/40 font-sans relative flex flex-col"
    >
      {/* Brand Geometric Background Accents */}
      <motion.div
        suppressHydrationWarning
        style={{ y: y1 }}
        className="fixed top-[-10%] right-[-5%] w-[600px] h-[600px] rounded-full border-[40px] border-[#1B36FF]/20 pointer-events-none z-0"
      />
      <motion.div
        suppressHydrationWarning
        style={{ y: y2 }}
        className="fixed bottom-[-20%] left-[-10%] w-[800px] h-[800px] rounded-full border-[80px] border-[#A4BDE5]/5 pointer-events-none z-0"
      />

      {/* Floating Orbs for Dynamism */}
      <motion.div
        suppressHydrationWarning
        animate={{ y: [0, -30, 0], x: [0, 20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="fixed top-[20%] left-[10%] w-64 h-64 bg-[#1B36FF]/10 rounded-full blur-[60px] pointer-events-none z-0"
      />
      <motion.div
        suppressHydrationWarning
        animate={{ y: [0, 40, 0], x: [0, -30, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="fixed bottom-[30%] right-[15%] w-80 h-80 bg-[#56CFFC]/10 rounded-full blur-[80px] pointer-events-none z-0"
      />

      {/* Dynamic Spotlight */}
      <motion.div
        suppressHydrationWarning
        className="fixed w-[600px] h-[600px] rounded-full bg-[#1B36FF]/20 blur-[100px] pointer-events-none z-0"
        style={{
          x: useTransform(springX, x => x - 300),
          y: useTransform(springY, y => y - 300),
        }}
      />
      
      {/* Subtle Grid Overlay */}
      <div className="fixed inset-0 bg-[url('/noise.svg')] opacity-10 mix-blend-overlay pointer-events-none z-0"></div>
      {/* Navigation — shared component (identical on landing page) */}
      <Navbar ref={navRef} activeNav={activeNav} onScrollToId={scrollToId} />
      {/* Hero Section — memoized to prevent LCP repaint from state updates */}
      <HeroSection />
      {/* Why India, Why Voqals Section */}
      <div ref={whyIndiaSectionRef}>
        <WhyIndiaSection whyIndiaPlaying={whyIndiaPlaying} setWhyIndiaPlaying={setWhyIndiaPlaying} whyIndiaProgress={whyIndiaProgress} whyIndiaDuration={whyIndiaDuration} />
      </div>
      {/* The Voqals Advantage Section */}
      <TheDifferenceSection onOpenTalentForm={() => navigate('/voice-talent/')} onOpenStudioForm={() => setIsStudioFormOpen(true)} />
      {/* Use Cases — buyer-orientation step between why-Voqals and what-we-do */}
      <UseCasesSection />
      {/* Custom Data Collection Section */}
      <section id="process" className="min-h-[100dvh] w-full shrink-0 relative flex flex-col items-center px-[var(--px-container)] pt-20 md:pt-24 pb-0 overflow-hidden">
        <CustomDataCollection onStartProject={() => setIsProjectDrawerOpen(true)} />
      </section>
      {/* Data Collection Pipeline Section */}
      <section id="pipeline" className="w-full relative flex flex-col items-center text-center justify-center md:flex md:items-center md:justify-center min-h-[100dvh] md:min-h-screen pt-24 pb-8 md:pt-[80px] md:pb-0 shrink-0 md:overflow-visible overflow-hidden">
        <DataCollectionPipeline onStartProject={() => setIsProjectDrawerOpen(true)} />
      </section>
      {/* Ready To Use Datasets Section */}
      <ReadyToUseDatasets />
      {/* Contact & Scoping Section (includes Footer) */}
      <ContactScoping />
    </div>
    {/* Project Brief — unified overlay for desktop & mobile */}
    <ProjectBriefOverlay isOpen={isProjectDrawerOpen} onClose={() => setIsProjectDrawerOpen(false)} />
    <StudioFormOverlay isOpen={isStudioFormOpen} onClose={() => setIsStudioFormOpen(false)} />
  </>
  );
}
